import React, { useCallback, useEffect, useRef, useState } from "react";
import Phaser from "phaser";
import { Socket } from "socket.io-client";
import { OceanScene } from "../game/scenes/OceanScene";
import {
  playSound,
  setMusicEnabled,
  SFX_KEYS,
  getSoundManager,
} from "../game/systems/SoundManager";
import { InventoryPanel } from "./InventoryPanel";
import { MarketplacePanel } from "./MarketplacePanel";
import { MarketplaceOverlay } from "./MarketplaceOverlay";
import { ShipyardPanel } from "./ShipyardPanel";
import { AdminPanel } from "./AdminPanel";
import { DebugPanel } from "./DebugPanel";
import { ModeratorPanel } from "./ModeratorPanel";
import { useAuth } from "../contexts/AuthContext";
import { getRaidFlavorText, getRaidTitle } from "../utils/raidFlavor";
import { IslandResearchPanel } from "./IslandResearchPanel";
import { TerritoryClaimPanel } from "./TerritoryClaimPanel";
import { SiegePanel } from "./SiegePanel";
import { FishingSpotResearchPanel } from "./FishingSpotResearchPanel";
import { ResourceNodeResearchPanel } from "./ResourceNodeResearchPanel";
import { WorldHistoryPanel } from "./WorldHistoryPanel";
import { GameCompass } from "./GameCompass";
import { GameMinimap } from "./GameMinimap";
import { SkillsPanel } from "./SkillsPanel";
import { ShipFittingPanel } from "./ShipFittingPanel";
import { CraftingPanel } from "./CraftingPanel";
import { LootContainerPanel } from "./LootContainerPanel";
import { FeedbackModal } from "./FeedbackModal";
import { TerritoryManagementPanel } from "./TerritoryManagementPanel";
import { TerritoryOverviewPanel } from "./TerritoryOverviewPanel";
import { TerritoryStatusPanel } from "./TerritoryStatusPanel";
import { WhirlpoolResearchPanel } from "./WhirlpoolResearchPanel";
import { RiftScarResearchPanel } from "./RiftScarResearchPanel";
import { WhirlpoolHUD } from "./WhirlpoolHUD";
import { WildcardPowerPanel } from "./WildcardPowerPanel";
import { SignaturePowerBar } from "./SignaturePowerBar";
import { RiftRelicPanel } from "./RiftRelicPanel";
import { PlayerExpressionPanel } from "./PlayerExpressionPanel";
import { FleetPanel } from "./FleetPanel";
import { CourierPanel } from "./CourierPanel";
import { ReferralPanel } from "./ReferralPanel";
import { DockedPanel } from "./DockedPanel";
import { DockServicesPanel } from "./DockServicesPanel";
import { TavernPanel } from "./TavernPanel";
import { QuestTrackerPanel } from "./QuestTrackerPanel";
import { QuestLogPanel } from "./QuestLogPanel";
import { QuestsPanel } from "./QuestsPanel";
import { QuestJournal } from "./QuestJournal";
import { NPCDialogueModal } from "./NPCDialogueModal";
import { PlayerInbox } from "./PlayerInbox";
import { STARTER_VOYAGE_QUESTS } from "../types/quests";
import { AmbientTradePanel } from "./AmbientTradePanel";
import { BankPanel } from "./BankPanel";
import { CookingPanel } from "./CookingPanel";
import { SmithingPanel } from "./SmithingPanel";
import { AlchemyPanel } from "./AlchemyPanel";
import { ConstructionHotbar } from "./ConstructionHotbar";
import { SkillLevelUpNotification } from "./SkillLevelUpNotification";
import { HarvestProgressOverlay } from "./HarvestProgressOverlay";
import { HarvestRewardToast } from "./HarvestRewardToast";
import { BottleContentModal } from "../ui/BottleContentModal";
import { ShipInvestigationPanel } from "../ui/ShipInvestigationPanel";
import { WorldEventNotification } from "./WorldEventNotification";
import { CombatCooldownTracker } from "./CombatCooldownTracker";
import { CannonFocusOverlay } from "./CannonFocusOverlay";
import { FishingMinigame } from "./FishingMinigame";
import { MiningMinigame } from "./MiningMinigame";
import { GameDailyRewardsPanel } from "./GameDailyRewardsPanel";
import { ServerFeedbackPanel } from "./ServerFeedbackPanel";
import { RiftNavigatorWrapper } from "./RiftNavigatorWrapper";
import { contextMenuEmitter } from "./GlobalContextMenu";
import { territoryOverviewEmitter } from "./TerritoryOverviewPanel";
import { ContextMenuRenderer } from "./ContextMenuRenderer";
import { DisconnectionOverlay } from "./DisconnectionOverlay";
import { DuplicateConnectionOverlay } from "./DuplicateConnectionOverlay";
import { playSanctumTerminalSound } from "../game/systems/SoundManager";
import { saveGuestSession } from "../utils/guestSession";
import { Hotbar } from "./Hotbar";
import { useHotbar } from "../hooks/useHotbar";
import { GlobalTooltipProvider } from "./GlobalTooltip";
import useAutomaticSync from "../hooks/useAutomaticSync";
import { OptionsMenu } from "./OptionsMenu";
import {
  type AmbientTradeItem,
  type HarvestDrop,
  type PirateRaidData,
  type GameCanvasProps,
} from "./GameCanvas/types";
import {
  WORLD_CHUNKS,
  REGIONS,
  getGameSize,
  getChunkRegion,
} from "./GameCanvas/constants";
import { useGameCanvasSocket } from "./GameCanvas/hooks/useGameCanvasSocket";
import { useGameCanvasState } from "./GameCanvas/hooks/useGameCanvasState";
import { useSanctumSocket } from "../hooks/useSanctumSocket";
import { ArchitectConduit } from "./ArchitectConduit";
import { ShipTooltip } from "./ShipTooltip";
import { WhirlpoolTooltip } from "./WhirlpoolTooltip";
import { LoadingOverlay } from "./LoadingOverlay";
import { WhirlpoolEntryData } from "../game/systems/WhirlpoolRenderer";
import { ResourceNodeTooltip } from "./ResourceNodeTooltip";

export const GameCanvas: React.FC<GameCanvasProps> = ({
  playerInfo,
  onOpenWorldMap,
  onOpenFactionWarfare,
  destination,
  onClearDestination,
  onSetDestination,
  onReady,
  onRiftStormsChange,
  onPirateRaidsChange,
  onSeaMonstersChange,
  onRiftScarsChange,
  onShowTutorial,
}) => {
  const auth = useAuth();
  const isAdmin = auth.accountInfo?.status === "admin";
  const isModerator = auth.accountInfo?.status === "moderator" || isAdmin;

  const gameRef = useRef<Phaser.Game | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Enable automatic sync (caching, optimistic updates, entity sync)
  useAutomaticSync({ socket: socketRef.current, enabled: true });

  // State management hook - replaces all useState calls
  const {
    // Player state
    playerId,
    setPlayerId,
    shipCount,
    setShipCount,
    position,
    setPosition,
    tilePos,
    setTilePos,
    chunkPos,
    setChunkPos,
    playerHp,
    setPlayerHp,
    playerSP,
    setPlayerSP,
    inCombat,
    setInCombat,
    pieces,
    setPieces,
    xp,
    setXp,
    level,
    setLevel,
    infamy,
    setInfamy,
    connected,
    setConnected,
    unlockedBlueprints,
    setUnlockedBlueprints,
    shipId,
    setShipId,
    shipType,
    setShipType,
    currentRegion,
    setCurrentRegion,
    securityTier,
    setSecurityTier,
    // Panel states
    inventoryOpen,
    setInventoryOpen,
    shopOpen,
    setShopOpen,
    currentTradingPost,
    setCurrentTradingPost,
    shipyardOpen,
    setShipyardOpen,
    currentShipyard,
    setCurrentShipyard,
    fleetPanelOpen,
    setFleetPanelOpen,
    expressionPanelOpen,
    setExpressionPanelOpen,
    debugPanelOpen,
    setDebugPanelOpen,
    moderatorPanelOpen,
    setModeratorPanelOpen,
    researchPanelOpen,
    setResearchPanelOpen,
    researchIslandId,
    setResearchIslandId,
    researchIslandPosition,
    setResearchIslandPosition,
    researchRefreshNonce,
    setResearchRefreshNonce,
    tavernOpen,
    setTavernOpen,
    bankOpen,
    setBankOpen,
    questLogOpen,
    setQuestLogOpen,
    questsPanelOpen,
    setQuestsPanelOpen,
    questJournalOpen,
    setQuestJournalOpen,
    selectedQuestForJournal,
    setSelectedQuestForJournal,
    trackedQuestId,
    setTrackedQuestId,
    inboxOpen,
    setInboxOpen,
    ambientTradeOpen,
    setAmbientTradeOpen,
    feedbackModalOpen,
    setFeedbackModalOpen,
    bottleModalOpen,
    setBottleModalOpen,
    shipInvestigationOpen,
    setShipInvestigationOpen,
    fishingResearchOpen,
    setFishingResearchOpen,
    resourceResearchOpen,
    setResourceResearchOpen,
    lootOpen,
    setLootOpen,
    worldHistoryOpen,
    setWorldHistoryOpen,
    infamyInfoOpen,
    setInfamyInfoOpen,
    reputationOpen,
    setReputationOpen,
    chatOpen,
    setChatOpen,
    skillsOpen,
    setSkillsOpen,
    fittingOpen,
    setFittingOpen,
    craftingOpen,
    setCraftingOpen,
    cookingOpen,
    setCookingOpen,
    smithingOpen,
    setSmithingOpen,
    alchemyOpen,
    setAlchemyOpen,
    constructionHotbarActive,
    setConstructionHotbarActive,
    currentConstructionKitId,
    setCurrentConstructionKitId,
    territoryClaimOpen,
    setTerritoryClaimOpen,
    claimIslandData,
    setClaimIslandData,
    siegePanelOpen,
    setSiegePanelOpen,
    siegeIslandId,
    setSiegeIslandId,
    siegeIslandName,
    setSiegeIslandName,
    territoryManagementOpen,
    setTerritoryManagementOpen,
    managementIslandId,
    setManagementIslandId,
    managementIslandName,
    setManagementIslandName,
    managementIsOwner,
    setManagementIsOwner,
    territoryStatusOpen,
    setTerritoryStatusOpen,
    statusIslandId,
    setStatusIslandId,
    statusIslandName,
    setStatusIslandName,
    whirlpoolResearchOpen,
    setWhirlpoolResearchOpen,
    riftScarData,
    setRiftScarData,
    signaturePanelOpen,
    setSignaturePanelOpen,
    optionsOpen,
    setOptionsOpen,
    referralPanelOpen,
    setReferralPanelOpen,
    dailyRewardsPanelOpen,
    setDailyRewardsPanelOpen,
    courierPanelOpen,
    setCourierPanelOpen,
    riftRelicPanelOpen,
    setRiftRelicPanelOpen,
    dockServicesPanelOpen,
    setDockServicesPanelOpen,
    currentShipHeat,
    maxShipHeat,
    // Docking state
    isDocked,
    setIsDocked,
    dockedIslandId,
    setDockedIslandId,
    dockedIslandName,
    setDockedIslandName,
    dockedIslandHasShop,
    setDockedIslandHasShop,
    dockedIslandHasShipyard,
    setDockedIslandHasShipyard,
    dockedIslandIsHarborOffice,
    setDockedIslandIsHarborOffice,
    dockedIslandHasTavern,
    setDockedIslandHasTavern,
    dockedIslandHasCourier,
    setDockedIslandHasCourier,
    dockedIslandHasDockServices,
    setDockedIslandHasDockServices,
    dockedIslandIsPermanent,
    setDockedIslandIsPermanent,
    dockPrompt,
    setDockPrompt,
    dockedIslandNpcs,
    setDockedIslandNpcs,
    // UI state
    toastQueue,
    removeToast,
    enqueueToast,
    worldEvents,
    removeWorldEvent,
    addWorldEvent,
    combatCooldowns,
    kickMessage,
    setKickMessage,
    isTypingInInput,
    setIsTypingInInput,
    // Research/Interaction state
    fishingSpotId,
    setFishingSpotId,
    fishingSpotType,
    setFishingSpotType,
    fishingSpotPosition,
    setFishingSpotPosition,
    resourceNodeId,
    setResourceNodeId,
    resourceNodeType,
    setResourceNodeType,
    resourceNodePosition,
    setResourceNodePosition,
    lootContainerId,
    setLootContainerId,
    whirlpoolId,
    setWhirlpoolId,
    whirlpoolPosition,
    setWhirlpoolPosition,
    investigatedShip,
    setInvestigatedShip,
    bottleContent,
    setBottleContent,
    npcDialogueOpen,
    setNpcDialogueOpen,
    npcDialogueData,
    setNpcDialogueData,
    ambientTradeData,
    setAmbientTradeData,
    harvestProgress,
    setHarvestProgress,
    harvestReward,
    setHarvestReward,
    // Loading overlay state
    loadingOverlayVisible,
    setLoadingOverlayVisible,
    loadingOverlayMessage,
    setLoadingOverlayMessage,
    isInstanceEntry,
    setIsInstanceEntry,
    // Game state
    cannonFocusActive,
    setCannonFocusActive,
    cannonFocusTarget,
    setCannonFocusTarget,
    inWhirlpool,
    setInWhirlpool,
    whirlpoolInstance,
    setWhirlpoolInstance,
    whirlpoolExpiresAt,
    setWhirlpoolExpiresAt,
    inRift,
    setInRift,
    currentRiftId,
    setCurrentRiftId,
    riftMetadata,
    setRiftMetadata,
    inSanctum,
    setInSanctum,
    setCurrentSanctumId,
    sanctumMetadata,
    setSanctumMetadata,
    riftStorms,
    setRiftStorms,
    pirateRaids,
    setPirateRaids,
    seaMonsters,
    setSeaMonsters,
    riftScars,
    setRiftScars,
    setInRiftStorm,
    setRiftStormBonus,
    hasEssenceEquipped,
    setHasEssenceEquipped,
    currentRiftStormId,
    setCurrentRiftStormId,
    currentRiftStormTier,
    setCurrentRiftStormTier,
    currentRiftStormLootMultiplier,
    setCurrentRiftStormLootMultiplier,
    currentRiftStormXpMultiplier,
    setCurrentRiftStormXpMultiplier,
    // Faction reputation state
    factionReputation,
    setFactionReputation,
    factionTiers,
    setFactionTiers,
    factionDailyGains,
    setFactionDailyGains,
    factionDailyCaps,
    setFactionDailyCaps,
    // Settings state
    volume,
    setVolume,
    cameraZoom,
    setCameraZoom,
    showPlayerIdentityUI,
    setShowPlayerIdentityUI,
    showMyNameLabel,
    setShowMyNameLabel,
    showMyFlag,
    setShowMyFlag,
    showOtherPlayerNames,
    setShowOtherPlayerNames,
    showOtherPlayerFlags,
    setShowOtherPlayerFlags,
    backgroundMusicEnabled,
    setBackgroundMusicEnabled,
    sfxVolume,
    setSfxVolume,
    musicVolume,
    setMusicVolume,
    worldEventNotificationsEnabled,
    setWorldEventNotificationsEnabled,
    serverFeedbackEnabled,
    setServerFeedbackEnabled,
    guestOnrampDismissed,
    setGuestOnrampDismissed,
    // Quest state
    starterVoyageMinimized,
    setStarterVoyageMinimized,
    starterVoyageHidden,
    setStarterVoyageHidden,
    chatMessage,
    setChatMessage,
    // Other state
    apiBaseUrl,
    setApiBaseUrl,
    researchCanDock,
    researchIsDocked,
    researchIsDockedHere,
    socketForHotbar,
    setSocketForHotbar,
    inventoryItems,
    setInventoryItems,
    hotbarVisible,
    setHotbarVisible,
    isDisconnected,
    setIsDisconnected,
    reconnectAttempt,
    setReconnectAttempt,
    duplicateConnectionError,
    setDuplicateConnectionError,
    currentWeather,
    setCurrentWeather,
    shipTooltip,
    setShipTooltip,
    // Refs
    isDockedRef,
    dockedIslandIdRef,
    researchIslandIdRef,
    researchPanelOpenRef,
    // Helpers
    closeDockedOverlays,
  } = useGameCanvasState({ savedStats: playerInfo.savedStats, socketRef });

  // Hotbar state
  const { hotbarConfig, updateSlot, useSlot } = useHotbar(socketForHotbar);

  // Sanctums state
  const { sanctumState } = useSanctumSocket(socketRef.current, gameRef);

  // Additional refs not in hook
  const didCallReadyRef = useRef<boolean>(false);
  const phaserReadyRef = useRef<boolean>(false);
  const hasReceivedLocalShipRef = useRef<boolean>(false);
  const devAdminPanelInstanceKeyRef = useRef<number>(Date.now());

  // Unread mail tracking
  const [unreadMailCount, setUnreadMailCount] = React.useState<number>(0);

  // Rift Navigator state
  const [riftNavigatorOpen, setRiftNavigatorOpen] =
    React.useState<boolean>(false);

  // Memory Lattice state
  const [memoryLatticeOpen, setMemoryLatticeOpen] =
    React.useState<boolean>(false);

  // Whirlpool tooltip state
  const [whirlpoolTooltip, setWhirlpoolTooltip] =
    React.useState<WhirlpoolEntryData | null>(null);
  const [whirlpoolTooltipVisible, setWhirlpoolTooltipVisible] =
    React.useState<boolean>(false);

  // Resource node tooltip state
  const [resourceNodeTooltip, setResourceNodeTooltip] = React.useState<{
    name: string;
    requirement?: string;
    x: number;
    y: number;
  } | null>(null);
  const [resourceNodeTooltipVisible, setResourceNodeTooltipVisible] =
    React.useState<boolean>(false);

  // Fishing minigame state
  const [fishingMinigameActive, setFishingMinigameActive] =
    useState<boolean>(false);
  const [miningMinigameActive, setMiningMinigameActive] =
    useState<boolean>(false);
  const [miningNodeId, setMiningNodeId] = useState<string | null>(null);
  const [miningNodeName, setMiningNodeName] = useState<string>("");
  const [miningDurationSeconds, setMiningDurationSeconds] = useState<number>(0);
  const [miningNodeLevel, setMiningNodeLevel] = useState<number>(1);
  const [fishingSpotTypeForMinigame, setFishingSpotTypeForMinigame] =
    React.useState<string>("shallow_waters");
  const [fishingSpotIdForMinigame, setFishingSpotIdForMinigame] =
    React.useState<string>("");
  const [sidebarMenuOpen, setSidebarMenuOpen] = React.useState<boolean>(false);
  const sidebarMenuRef = useRef<HTMLDivElement>(null);

  // Ship upgrade state
  const [isUpgradingShip, setIsUpgradingShip] = useState<boolean>(false);

  useEffect(() => {
    isDockedRef.current = isDocked;
  }, [isDocked, isDockedRef]);

  useEffect(() => {
    dockedIslandIdRef.current = dockedIslandId;
  }, [dockedIslandId, dockedIslandIdRef]);

  useEffect(() => {
    if (!isDocked) {
      closeDockedOverlays();
      setDockPrompt(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeDockedOverlays, isDocked, setDockPrompt]);

  useEffect(() => {
    researchIslandIdRef.current = researchIslandId;
  }, [researchIslandId, researchIslandIdRef]);

  useEffect(() => {
    researchPanelOpenRef.current = researchPanelOpen;
  }, [researchPanelOpen, researchPanelOpenRef]);

  useEffect(() => {
    const handleSidebarMenuClick = (event: MouseEvent) => {
      if (
        sidebarMenuRef.current &&
        !sidebarMenuRef.current.contains(event.target as Node)
      ) {
        setSidebarMenuOpen(false);
      }
    };

    if (sidebarMenuOpen) {
      document.addEventListener("mousedown", handleSidebarMenuClick);
      return () =>
        document.removeEventListener("mousedown", handleSidebarMenuClick);
    }
  }, [sidebarMenuOpen]);

  useEffect(() => {
    setMusicEnabled(backgroundMusicEnabled);
    try {
      localStorage.setItem(
        "backgroundMusicEnabled",
        String(backgroundMusicEnabled),
      );
    } catch {
      // ignore
    }
  }, [backgroundMusicEnabled]);

  useEffect(() => {
    try {
      localStorage.setItem("sfxVolume", String(sfxVolume / 100));
    } catch {
      // ignore
    }
    const soundManager = getSoundManager();
    if (soundManager) {
      soundManager.setSfxVolume(sfxVolume / 100);
    }
  }, [sfxVolume]);

  useEffect(() => {
    try {
      localStorage.setItem("musicVolume", String(musicVolume / 100));
    } catch {
      // ignore
    }
    const soundManager = getSoundManager();
    if (soundManager) {
      soundManager.setMusicVolume(musicVolume / 100);
    }
  }, [musicVolume]);

  // Apply camera zoom
  useEffect(() => {
    const oceanScene = gameRef.current?.scene.getScene("OceanScene") as any;
    if (oceanScene?.cameras?.main) {
      const zoomLevel = cameraZoom / 100;
      oceanScene.cameras.main.setZoom(zoomLevel);
    }
    try {
      localStorage.setItem("cameraZoom", String(cameraZoom));
    } catch {
      // ignore
    }
  }, [cameraZoom]);

  // Refresh ship visibility when player identity settings change
  useEffect(() => {
    const oceanScene = gameRef.current?.scene.getScene("OceanScene") as any;
    if (oceanScene?.shipRenderer?.refreshAllVisibility) {
      oceanScene.shipRenderer.refreshAllVisibility();
    }
  }, [showMyNameLabel, showMyFlag, showOtherPlayerNames, showOtherPlayerFlags]);

  // Persist hotbar visibility preference
  useEffect(() => {
    try {
      localStorage.setItem("hotbarVisible", String(hotbarVisible));
    } catch {
      // ignore
    }
  }, [hotbarVisible]);

  // Q keybinding for Quest Log Panel
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent): void => {
      if ((e.key === "q" || e.key === "Q") && !isTypingInInput) {
        e.preventDefault();
        setQuestLogOpen((prev) => !prev);
      }
    };

    const handleFocus = (e: FocusEvent): void => {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        setIsTypingInInput(true);
      }
    };

    const handleBlur = (): void => {
      setIsTypingInInput(false);
    };

    window.addEventListener("keydown", handleKeyPress);
    window.addEventListener("focus", handleFocus, true);
    window.addEventListener("blur", handleBlur, true);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      window.removeEventListener("focus", handleFocus, true);
      window.removeEventListener("blur", handleBlur, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTypingInInput, setIsTypingInInput, setQuestLogOpen]);

  // Notify parent when rift storms change (for world map)
  useEffect(() => {
    if (onRiftStormsChange) {
      onRiftStormsChange(riftStorms);
    }
  }, [riftStorms, onRiftStormsChange]);

  useEffect(() => {
    if (onPirateRaidsChange) {
      onPirateRaidsChange(pirateRaids);
    }
  }, [pirateRaids, onPirateRaidsChange]);

  useEffect(() => {
    if (onSeaMonstersChange) {
      onSeaMonstersChange(seaMonsters);
    }
  }, [seaMonsters, onSeaMonstersChange]);

  useEffect(() => {
    if (onRiftScarsChange) {
      onRiftScarsChange(riftScars);
    }
  }, [riftScars, onRiftScarsChange]);

  // Listen for resource node tooltip events
  useEffect(() => {
    const setupResourceNodeListeners = () => {
      const oceanScene = gameRef.current?.scene.getScene("OceanScene");
      if (!oceanScene) {
        setTimeout(setupResourceNodeListeners, 100);
        return;
      }

      oceanScene.events.on(
        "resource:tooltip:show",
        (data: {
          name: string;
          requirement?: string;
          x: number;
          y: number;
        }) => {
          setResourceNodeTooltip(data);
          setResourceNodeTooltipVisible(true);
        },
      );

      oceanScene.events.on("resource:tooltip:hide", () => {
        setResourceNodeTooltipVisible(false);
      });
    };

    setupResourceNodeListeners();
  }, []);

  // Listen for instance entry events and show loading overlay
  useEffect(() => {
    const handleShowOverlay = (message: string, isInstance: boolean) => {
      console.log("📍 Loading overlay triggered:", message);
      setIsInstanceEntry(isInstance);
      setLoadingOverlayMessage(message);
      setLoadingOverlayVisible(true);
      setTimeout(() => {
        console.log("📍 Hiding loading overlay");
        setLoadingOverlayVisible(false);
      }, 3000);
    };

    const setupListeners = () => {
      const oceanScene = gameRef.current?.scene.getScene("OceanScene");
      if (!oceanScene) {
        console.log("⚠️ OceanScene not found, retrying...");
        setTimeout(setupListeners, 100);
        return;
      }

      console.log("✅ OceanScene found, attaching listeners");

      oceanScene.events.on("whirlpool:entered", () =>
        handleShowOverlay("Entering whirlpool...", true),
      );
      oceanScene.events.on("whirlpool:exited", () =>
        handleShowOverlay("Exiting whirlpool...", true),
      );
      oceanScene.events.on("rift:entered", () =>
        handleShowOverlay("Entering rift...", true),
      );
      oceanScene.events.on("rift:exited", () =>
        handleShowOverlay("Exiting rift...", true),
      );
      oceanScene.events.on("sanctum:entered", () =>
        handleShowOverlay("Entering sanctum...", true),
      );
      oceanScene.events.on("sanctum:exited", () =>
        handleShowOverlay("Exiting sanctum...", true),
      );

      console.log("✅ All instance entry listeners attached");
    };

    setupListeners();
  }, [setLoadingOverlayVisible, setLoadingOverlayMessage, setIsInstanceEntry]);

  // Listen for signature cast mode events
  useEffect(() => {
    const handleSignatureCastMode = (event: CustomEvent) => {
      const { slotIndex } = event.detail;
      const oceanScene = gameRef.current?.scene.getScene("OceanScene") as any;
      if (!oceanScene) {
        console.error("⚡ [ERROR] OceanScene not found");
        return;
      }

      // Get equipped signature move from hotbar config
      const slot = hotbarConfig.slots?.[slotIndex];
      if (!slot || !slot.item) {
        console.error(`⚡ [ERROR] No item in hotbar slot ${slotIndex}`);
        return;
      }

      // Validate moveId exists and is not null
      const moveId = slot.item.itemId;
      console.log(
        `⚡ [DEBUG] Hotbar slot ${slotIndex} itemId: "${moveId}" (type: ${typeof moveId})`,
      );
      console.log(`⚡ [DEBUG] Full slot data:`, JSON.stringify(slot.item));

      if (!moveId || moveId === null || moveId === "null" || moveId === "") {
        console.error(
          `⚡ [ERROR] Invalid moveId in slot ${slotIndex}: "${moveId}"`,
        );
        // Show user-facing error
        if (socketRef.current) {
          socketRef.current.emit("chat:message", {
            type: "error",
            message:
              "❌ No signature move equipped - equip one from the Signature Moves panel first",
          });
        }
        return;
      }

      // For signature moves, extract color and aoeRadius from the move ID
      // Signature move IDs are formatted like: flame_strike, storm_surge, etc.
      const moveName = moveId
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      // Map element to color
      const elementColorMap: Record<string, string> = {
        flame: "#ff6b35",
        storm: "#00bfff",
        iron: "#a9a9a9",
        shadow: "#8b008b",
      };
      const element = moveId.split("_")[0];
      const moveColor = elementColorMap[element] || "#4fc3f7";
      const aoeRadius = 3; // Default AoE radius for all signature moves

      console.log(
        `⚡ Entering signature cast mode for ${moveName} (${moveId})`,
      );
      oceanScene.enterSignatureCastMode(moveId, moveName, moveColor, aoeRadius);
    };

    window.addEventListener(
      "wildcard:enter-cast-mode",
      handleSignatureCastMode as EventListener,
    );
    return () =>
      window.removeEventListener(
        "wildcard:enter-cast-mode",
        handleSignatureCastMode as EventListener,
      );
  }, [hotbarConfig]);

  const callReadyIfPossible = useCallback((): void => {
    if (didCallReadyRef.current) return;
    if (!phaserReadyRef.current) return;
    if (!hasReceivedLocalShipRef.current) return;
    didCallReadyRef.current = true;
    if (onReady) {
      onReady();
    }
  }, [onReady]);

  // Sync playerId from socket if state is stale (fallback for race conditions)
  useEffect(() => {
    const syncPlayerId = (): void => {
      if (!playerId && socketRef.current?.connected && socketRef.current?.id) {
        console.log(
          `🔄 Syncing stale playerId from socket: ${socketRef.current.id}`,
        );
        setPlayerId(socketRef.current.id);
      }
    };

    // Check immediately and then every 500ms
    syncPlayerId();
    const interval = setInterval(syncPlayerId, 500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, setPlayerId]);

  // Keyboard handler for opening inventory with 'I', world map with 'M', debug panel with 'F3', and chat with Enter
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Check if user is typing in an input field - block all keybinds except Escape
      const activeElement = document.activeElement;
      const isTypingInInput =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          (activeElement as HTMLElement).isContentEditable);

      // If typing in an input, only allow Escape to blur/close
      if (isTypingInInput && e.key !== "Escape") {
        return;
      }

      // Note: Panel hotkeys are allowed during combat - the minigame UI will handle
      // its own input blocking if needed. Don't block keybinds here.

      // If chat is open, handle chat-specific keys
      if (chatOpen) {
        if (e.key === "Escape") {
          e.preventDefault();
          setChatOpen(false);
          setChatMessage("");
          return;
        }
        // Don't process other keys while chat is open (let input handle them)
        return;
      }

      // Esc: close any open modal/panel
      if (e.key === "Escape") {
        contextMenuEmitter.hide();

        if (miningMinigameActive) {
          e.preventDefault();
          setMiningMinigameActive(false);
          if (miningNodeId) {
            socketRef.current?.emit("resource:cancelHarvest", {
              nodeId: miningNodeId,
            });
            setMiningNodeId(null);
          }
          return;
        }

        if (fishingMinigameActive) {
          e.preventDefault();
          setFishingMinigameActive(false);
          return;
        }

        if (cannonFocusActive) {
          e.preventDefault();
          setCannonFocusActive(false);
          const oceanScene = gameRef.current?.scene.getScene(
            "OceanScene",
          ) as any;
          oceanScene?.setCannonFocusActive(false);
          socketRef.current?.emit("cannonFocusUpdate", {
            multiplier: 1.0,
            defenseModifier: 1.0,
          });
          return;
        }

        if (riftNavigatorOpen) {
          e.preventDefault();
          setRiftNavigatorOpen(false);
          return;
        }

        if (optionsOpen) {
          e.preventDefault();
          setOptionsOpen(false);
          return;
        }

        let closed = false;
        if (inventoryOpen) {
          setInventoryOpen(false);
          closed = true;
        }
        if (shopOpen) {
          setShopOpen(false);
          closed = true;
        }
        if (shipyardOpen) {
          setShipyardOpen(false);
          closed = true;
        }
        if (lootOpen) {
          setLootOpen(false);
          setLootContainerId(null);
          closed = true;
        }
        if (researchPanelOpen) {
          setResearchPanelOpen(false);
          setResearchIslandId(null);
          setResearchIslandPosition(null);
          closed = true;
        }
        if (fishingResearchOpen) {
          setFishingResearchOpen(false);
          setFishingSpotId(null);
          setFishingSpotType(null);
          setFishingSpotPosition(null);
          closed = true;
        }
        if (resourceResearchOpen) {
          setResourceResearchOpen(false);
          setResourceNodeId(null);
          setResourceNodeType(null);
          setResourceNodePosition(null);
          closed = true;
        }
        if (whirlpoolResearchOpen) {
          setWhirlpoolResearchOpen(false);
          setWhirlpoolId(null);
          setWhirlpoolPosition(null);
          closed = true;
        }
        if (debugPanelOpen) {
          setDebugPanelOpen(false);
          closed = true;
        }
        if (moderatorPanelOpen) {
          setModeratorPanelOpen(false);
          closed = true;
        }
        if (skillsOpen) {
          setSkillsOpen(false);
          closed = true;
        }
        if (fittingOpen) {
          setFittingOpen(false);
          closed = true;
        }
        if (craftingOpen) {
          setCraftingOpen(false);
          closed = true;
        }
        if (cookingOpen) {
          setCookingOpen(false);
          closed = true;
        }
        if (signaturePanelOpen) {
          setSignaturePanelOpen(false);
          closed = true;
        }
        if (expressionPanelOpen) {
          setExpressionPanelOpen(false);
          closed = true;
        }
        if (fleetPanelOpen) {
          setFleetPanelOpen(false);
          closed = true;
        }
        if (courierPanelOpen) {
          setCourierPanelOpen(false);
          closed = true;
        }
        if (tavernOpen) {
          setTavernOpen(false);
          closed = true;
        }
        if (territoryClaimOpen) {
          setTerritoryClaimOpen(false);
          setClaimIslandData(null);
          closed = true;
        }
        if (siegePanelOpen) {
          setSiegePanelOpen(false);
          closed = true;
        }
        if (territoryManagementOpen) {
          setTerritoryManagementOpen(false);
          closed = true;
        }
        if (worldHistoryOpen) {
          setWorldHistoryOpen(false);
          closed = true;
        }
        if (infamyInfoOpen) {
          setInfamyInfoOpen(false);
          closed = true;
        }
        if (reputationOpen) {
          setReputationOpen(false);
          closed = true;
        }
        if (closed) {
          e.preventDefault();
          return;
        }

        // If nothing was open, Esc opens Options
        e.preventDefault();
        setOptionsOpen(true);
        return;
      }

      // I: Toggle inventory
      if (e.key === "i" || e.key === "I") {
        e.preventDefault();
        e.stopPropagation();
        setInventoryOpen(!inventoryOpen);
      }

      // H: Toggle hotbar visibility
      if (e.key === "h" || e.key === "H") {
        e.preventDefault();
        e.stopPropagation();
        setHotbarVisible(!hotbarVisible);
      }

      // Enter: Open chat input (only if no other panels are open)
      if (e.key === "Enter" && !chatOpen) {
        e.preventDefault();
        e.stopPropagation();
        setChatOpen(true);
        return;
      }
      // K: Open skills panel
      if (
        (e.key === "k" || e.key === "K") &&
        !inventoryOpen &&
        !shopOpen &&
        !skillsOpen &&
        !fittingOpen &&
        !craftingOpen
      ) {
        e.preventDefault();
        e.stopPropagation();
        setSkillsOpen(true);
      }
      // F: Open ship fitting panel
      if (
        (e.key === "f" || e.key === "F") &&
        !inventoryOpen &&
        !shopOpen &&
        !skillsOpen &&
        !fittingOpen &&
        !craftingOpen
      ) {
        e.preventDefault();
        e.stopPropagation();
        setFittingOpen(true);
      }
      // C: Open crafting panel
      if (
        (e.key === "c" || e.key === "C") &&
        !inventoryOpen &&
        !shopOpen &&
        !skillsOpen &&
        !fittingOpen &&
        !craftingOpen &&
        !cookingOpen &&
        !smithingOpen &&
        !alchemyOpen
      ) {
        e.preventDefault();
        e.stopPropagation();
        setCraftingOpen(true);
      }
      // P: Open player expression panel (identity/titles/flags)
      if (e.key === "p" || e.key === "P") {
        e.preventDefault();
        e.stopPropagation();
        setExpressionPanelOpen(!expressionPanelOpen);
      }
      // G: Open fleet panel
      if (
        (e.key === "g" || e.key === "G") &&
        !inventoryOpen &&
        !shopOpen &&
        !skillsOpen &&
        !fittingOpen &&
        !craftingOpen &&
        !expressionPanelOpen &&
        !fleetPanelOpen
      ) {
        e.preventDefault();
        e.stopPropagation();
        setFleetPanelOpen(true);
      }
      // M: Open world map (or rift navigator if in a rift)
      if (
        (e.key === "m" || e.key === "M") &&
        !inventoryOpen &&
        !shopOpen &&
        !skillsOpen &&
        !fittingOpen &&
        !craftingOpen
      ) {
        e.preventDefault();
        e.stopPropagation();
        if (inRift) {
          // In a rift - open rift navigator instead of world map
          setRiftNavigatorOpen(true);
          enqueueToast(
            "Use the Rift Navigator to view your location in rifts",
            "warning",
          );
        } else {
          onOpenWorldMap(position);
          // Emit map opened event for quest tracking
          socketRef.current?.emit("quest:mapOpened");
        }
      }
      // ]: Toggle Rift Navigator
      if (e.key === "]") {
        e.preventDefault();
        console.log("[RIFT NAVIGATOR] ] key pressed, toggling navigator");
        setRiftNavigatorOpen((prev) => !prev);
      }
      // F3 toggles debug panel
      if (e.key === "F3") {
        e.preventDefault();
        setDebugPanelOpen((prev) => !prev);
      }

      // F4 toggles moderator panel
      if (e.key === "F4") {
        e.preventDefault();
        if (isModerator) {
          setModeratorPanelOpen((prev) => !prev);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Setters from useState are stable
  }, [
    inventoryOpen,
    shopOpen,
    shipyardOpen,
    researchPanelOpen,
    fishingResearchOpen,
    resourceResearchOpen,
    debugPanelOpen,
    skillsOpen,
    fittingOpen,
    craftingOpen,
    expressionPanelOpen,
    showPlayerIdentityUI,
    optionsOpen,
    fleetPanelOpen,
    lootOpen,
    whirlpoolResearchOpen,
    worldHistoryOpen,
    infamyInfoOpen,
    reputationOpen,
    chatOpen,
    riftNavigatorOpen,
    position,
    onOpenWorldMap,
    isModerator,
    moderatorPanelOpen,
    inCombat,
    courierPanelOpen,
    tavernOpen,
    territoryClaimOpen,
    siegePanelOpen,
    territoryManagementOpen,
    signaturePanelOpen,
    isTypingInInput,
  ]); // eslint-disable-line react-hooks/exhaustive-deps -- Setters from useState are stable

  // Save guest session periodically (every 30 seconds) for non-authenticated players
  useEffect(() => {
    // Only save for guest players (no characterId means guest)
    if (playerInfo.characterId) return;

    const saveInterval = setInterval(() => {
      saveGuestSession({
        name: playerInfo.name,
        characterType: playerInfo.character,
        shipColor: playerInfo.shipColor,
        position,
        berries: pieces,
        level,
        xp,
        infamy,
      });
    }, 30000); // Save every 30 seconds

    // Also save on unmount
    return () => {
      clearInterval(saveInterval);
      saveGuestSession({
        name: playerInfo.name,
        characterType: playerInfo.character,
        shipColor: playerInfo.shipColor,
        position,
        berries: pieces,
        level,
        xp,
        infamy,
      });
    };
  }, [
    playerInfo.characterId,
    playerInfo.name,
    playerInfo.character,
    playerInfo.shipColor,
    position,
    pieces,
    level,
    xp,
    infamy,
  ]);

  // Block browser context menu on game canvas area and all game UI
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      // Allow context menu on inventory and hotbar panels
      if (
        target.closest(".inventory-panel") ||
        target.closest(".hotbar-slot")
      ) {
        return;
      }
      // Block context menu on game canvas, its container, and any game UI elements
      if (
        target.closest(".game-canvas-container") ||
        target.closest("#game-canvas") ||
        target.closest(".game-main") ||
        target.closest('[style*="position: fixed"]') // Block on modals/overlays
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu, true); // Use capture phase
    return () =>
      document.removeEventListener("contextmenu", handleContextMenu, true);
  }, []);

  // Expose socket to window for standalone panels
  useEffect(() => {
    if (socketRef.current) {
      (window as any).gameSocket = socketRef.current;
      console.log("✅ Socket exposed to window.gameSocket");
    }
  }, [socketRef]);

  // Handle rift restoration on login - update React UI state
  useEffect(() => {
    if (!socketRef.current) return;

    const socket = socketRef.current;

    const handleRiftRestored = async (data: {
      riftId: string;
      position: { x: number; y: number };
    }): Promise<void> => {
      console.log(
        `🌀 [REACT] Rift restored - updating UI state: ${data.riftId}`,
      );

      // Update React UI state immediately
      setInRift(true);
      setCurrentRiftId(data.riftId);

      // Fetch rift metadata from server
      try {
        const response = await fetch(`${apiBaseUrl}/api/rifts/${data.riftId}`);
        if (response.ok) {
          const riftData = await response.json();
          setRiftMetadata({
            id: riftData.id,
            name: riftData.name,
            layer: riftData.layer,
            anomalyLevel: riftData.anomaly_level || riftData.anomalyLevel,
            theme: riftData.theme,
            jumpsToCore: riftData.jumpsToCore,
          });
        }
      } catch (error) {
        console.error("Failed to fetch rift metadata:", error);
      }
    };

    socket.on("rift:restored", handleRiftRestored);

    // Listen for rift:exited from server (e.g., admin teleport to core world)
    const handleRiftExited = () => {
      console.log("🌀 [SOCKET] Rift exited event received from server");
      console.log(
        "🌀 [REACT] Setting inRift=false, currentRiftId=core, riftMetadata=null",
      );
      console.log("🌀 [DEBUG] gameRef.current:", gameRef.current);

      setInRift(false);
      setCurrentRiftId("core");
      setRiftMetadata(null);

      // Emit to OceanScene so it can update game state
      const oceanScene = gameRef.current?.scene.getScene("OceanScene");
      console.log("🌀 [DEBUG] oceanScene:", oceanScene);
      if (oceanScene) {
        console.log("🌀 [SCENE] Emitting rift:exited to OceanScene");
        oceanScene.events.emit("rift:exited");
      } else {
        console.warn(
          "🌀 [SCENE] OceanScene not found, cannot emit rift:exited",
        );
      }
    };

    console.log(
      "🌀 [SOCKET] Registering rift:exited listener on socket:",
      socket.id,
    );
    socket.on("rift:exited", handleRiftExited);

    return () => {
      socket.off("rift:restored", handleRiftRestored);
      socket.off("rift:exited", handleRiftExited);
    };
  }, [apiBaseUrl, setInRift, setCurrentRiftId, setRiftMetadata, gameRef]);

  // Track unread mail count
  useEffect(() => {
    if (!socketRef.current) return;

    const socket = socketRef.current;

    // Request initial unread count
    socket.emit("inbox:getUnreadCount");

    const handleUnreadCount = (data: { count: number }): void => {
      setUnreadMailCount(data.count);
    };

    const handleMessageRead = (): void => {
      // Refresh unread count when a message is read
      socket.emit("inbox:getUnreadCount");
    };

    const handleMessageDeleted = (): void => {
      // Refresh unread count when a message is deleted
      socket.emit("inbox:getUnreadCount");
    };

    const handleNewMessage = (): void => {
      // Refresh unread count when a new message arrives
      socket.emit("inbox:getUnreadCount");
    };

    socket.on("inbox:unreadCount", handleUnreadCount);
    socket.on("inbox:messageRead", handleMessageRead);
    socket.on("inbox:messageDeleted", handleMessageDeleted);
    socket.on("inbox:newMessage", handleNewMessage);

    return () => {
      socket.off("inbox:unreadCount", handleUnreadCount);
      socket.off("inbox:messageRead", handleMessageRead);
      socket.off("inbox:messageDeleted", handleMessageDeleted);
      socket.off("inbox:newMessage", handleNewMessage);
    };
  }, []);

  // Listen for skill level up events from server
  useEffect(() => {
    if (!socketRef.current) return;

    const socket = socketRef.current;

    const handleSkillLevelUp = (data: any) => {
      // Trigger notification through global method set by SkillLevelUpNotification component
      if ((window as any).__addSkillLevelUpNotification) {
        (window as any).__addSkillLevelUpNotification(data);
      }

      // Play sound if sound is enabled
      const soundManager = getSoundManager();
      if (soundManager) {
        playSound(SFX_KEYS.LEVEL_UP);
      }
    };

    socket.on("skill:levelUp", handleSkillLevelUp);

    return () => {
      socket.off("skill:levelUp", handleSkillLevelUp);
    };
  }, []);

  // Listen for skill XP events from server (optional, for future use)
  useEffect(() => {
    if (!socketRef.current || !socketRef.current.connected) return;

    const socket = socketRef.current;

    const handleSkillXP = (data: any) => {
      // Can be used for XP bar updates or other effects
      // For now, just log for debugging
      if (process.env.NODE_ENV === "development") {
        console.debug("📊 XP gained:", data);
      }
    };

    const handleCompassTarget = (data: any) => {
      // Set compass destination when server sends a target
      console.log(`🧭 [CLIENT] Received compass:target event:`, data);

      // If data is null, clear the compass
      if (data === null) {
        if (onClearDestination) {
          onClearDestination();
        }
        console.log(`🧭 [CLIENT] Compass cleared`);
        return;
      }

      // Typically happens when player spawns/respawns in a barrel
      if (
        data &&
        data.x !== undefined &&
        data.y !== undefined &&
        onSetDestination
      ) {
        console.log(
          `🧭 [CLIENT] Setting compass with onSetDestination callback`,
        );
        onSetDestination({
          x: data.x,
          y: data.y,
          name: data.name || "Unknown Location",
        });
        console.log(
          `🧭 [CLIENT] Compass set to: ${data.name} at (${data.x}, ${data.y})`,
        );
      } else {
        console.warn(
          `🧭 [CLIENT] Invalid compass data or missing onSetDestination:`,
          { data, hasCallback: !!onSetDestination },
        );
      }
    };

    const handleMinimapTarget = (data: {
      x: number;
      y: number;
      name?: string;
    }) => {
      if (!data || data.x === undefined || data.y === undefined) return;
      if (!onSetDestination) return;
      onSetDestination({
        x: data.x,
        y: data.y,
        name: data.name || "Target",
      });
    };

    const handleMinimapNearestResult = (data: {
      success: boolean;
      message?: string;
      target?: { x: number; y: number; name?: string };
    }) => {
      if (!data?.success || !data.target) {
        if (data?.message) {
          console.warn(`🧭 Minimap search failed: ${data.message}`);
        }
        return;
      }
      handleMinimapTarget({
        x: data.target.x,
        y: data.target.y,
        name: data.target.name,
      });
    };

    socket.on("skill:xp", handleSkillXP);
    socket.on("compass:target", handleCompassTarget);
    socket.on("minimap:nearestResult", handleMinimapNearestResult);
    gameRef.current?.events.on("minimap:setDestination", handleMinimapTarget);

    return () => {
      socket.off("skill:xp", handleSkillXP);
      socket.off("compass:target", handleCompassTarget);
      socket.off("minimap:nearestResult", handleMinimapNearestResult);
      gameRef.current?.events.off(
        "minimap:setDestination",
        handleMinimapTarget,
      );
    };
  }, [connected, onSetDestination, onClearDestination]);

  // Use socket handlers hook - handles all socket event listeners
  useGameCanvasSocket({
    socketRef,
    gameRef,
    playerInfo,
    setConnected,
    setPlayerId,
    setSocketForHotbar,
    setShipCount,
    setPosition,
    setTilePos,
    setChunkPos,
    setPlayerHp,
    setPlayerSP,
    setInCombat,
    setPieces,
    setXp,
    setLevel,
    setInfamy,
    setSecurityTier,
    setShipId,
    setShipType,
    setCurrentRegion,
    setIsDocked,
    setDockedIslandId,
    setUnlockedBlueprints,
    setHasEssenceEquipped,
    setSeaMonsters,
    setFactionReputation,
    setFactionTiers,
    setFactionDailyGains,
    setFactionDailyCaps,
    setResearchRefreshNonce,
    setKickMessage,
    setApiBaseUrl,
    setIsDisconnected,
    setReconnectAttempt,
    setDuplicateConnectionError,
    setCurrentWeather,
    setLoadingOverlayVisible,
    setLoadingOverlayMessage,
    setIsInstanceEntry,
    isDockedRef,
    dockedIslandIdRef,
    researchIslandIdRef,
    researchPanelOpenRef,
    hasReceivedLocalShipRef,
    phaserReadyRef,
    callReadyIfPossible,
    enqueueToast,
    addWorldEvent,
    getRaidTitle,
    getRaidFlavorText,
  });

  // Phaser game initialization - separate from socket handlers
  useEffect(() => {
    // Square game canvas - scales with window up to MAX_GAME_SIZE
    const initialSize = getGameSize();
    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: initialSize,
      height: initialSize,
      parent: "game-canvas",
      backgroundColor: "#0a1628",
      scene: [OceanScene],
      render: {
        antialias: true,
        pixelArt: false,
        roundPixels: false,
        clearBeforeRender: true,
        preserveDrawingBuffer: false,
        transparent: false,
        powerPreference: "high-performance",
      },
      scale: {
        mode: Phaser.Scale.NONE,
      },
      fps: {
        target: 60,
        forceSetTimeOut: false,
      },
    };

    gameRef.current = new Phaser.Game(config);

    // Expose game instance and socket to window for standalone panels
    (window as any).phaserGame = gameRef.current;

    const handleContextLost = (event: Event): void => {
      event.preventDefault();
      console.error("WebGL context lost - prompting refresh");
      setLoadingOverlayMessage("Rendering lost. Please refresh the page.");
      setLoadingOverlayVisible(true);
    };

    const handleContextRestored = (): void => {
      console.warn("WebGL context restored - prompting refresh");
      setLoadingOverlayMessage(
        "Rendering recovered. Please refresh to continue.",
      );
      setLoadingOverlayVisible(true);
    };

    const canvas = gameRef.current.canvas;
    if (canvas) {
      canvas.addEventListener(
        "webglcontextlost",
        handleContextLost as EventListener,
        false,
      );
      canvas.addEventListener(
        "webglcontextrestored",
        handleContextRestored as EventListener,
        false,
      );
    }

    // Handle window resize - scale game canvas up to MAX_GAME_SIZE
    const handleResize = (): void => {
      if (gameRef.current) {
        const newSize = getGameSize();
        gameRef.current.scale.resize(newSize, newSize);
      }
    };
    window.addEventListener("resize", handleResize);

    // Declare handlers outside if block so they can be cleaned up
    const handleConstructionEnterMode = (e: Event) => {
      const customEvent = e as CustomEvent<{
        kitItemId: string;
        kitName: string;
      }>;
      if (!customEvent.detail) return;

      const scene = gameRef.current?.scene.getScene("OceanScene") as OceanScene;
      if (!scene) {
        console.warn("🔨 [Construction] Scene not found");
        return;
      }

      if (scene?.enterConstructionMode) {
        scene.enterConstructionMode(
          customEvent.detail.kitItemId,
          customEvent.detail.kitName,
        );
      }
    };

    gameRef.current.events.on("ready", () => {
      const scene = gameRef.current?.scene.getScene("OceanScene") as OceanScene;
      if (scene) {
        phaserReadyRef.current = true;
        callReadyIfPossible();
        console.log(`Game ready`);

        // Apply default 1.5x zoom on scene ready
        const zoomLevel = cameraZoom / 100;
        if (scene.cameras?.main) {
          scene.cameras.main.setZoom(zoomLevel);
          console.log(`🎥 Applied initial camera zoom: ${zoomLevel}x`);
        }
        // Socket will be set in the 'connect' event handler, not here
        // This ensures setupSocketListeners() is called only when socket is actually connected

        // Listen for loot container open event from game
        gameRef.current?.events.on(
          "lootcontainer:open",
          (data: { id: string }) => {
            console.log(
              "📦 GameCanvas: Loot container open event received:",
              data.id,
            );
            console.log(
              "📦 GameCanvas: Setting lootOpen=true, lootContainerId=",
              data.id,
            );
            playSound(SFX_KEYS.LOOT_OPEN);
            setLootOpen(true);
            setLootContainerId(data.id);
          },
        );

        // Listen for bottle show event from game
        gameRef.current?.events.on(
          "bottle:show",
          (data: {
            type: string;
            content: string;
            reward?: {
              pieces?: number;
              itemId?: string;
              itemName?: string;
              treasureX?: number;
              treasureY?: number;
            };
          }) => {
            setBottleContent(data);
            setBottleModalOpen(true);
          },
        );

        // Listen for sanctum terminal open event from game
        gameRef.current?.events.on("sanctum:openTerminal", () => {
          console.log("🖥️ Opening Memory Lattice UI");
          playSanctumTerminalSound();
          setMemoryLatticeOpen(true);
        });

        // Listen for trading post interaction from scene
        scene.events.on("tradingpost:interact", (data: { name: string }) => {
          setCurrentTradingPost(data.name);
          setShopOpen(true);
        });

        // Listen for shipyard interaction from scene
        scene.events.on("shipyard:interact", (data: { name: string }) => {
          setCurrentShipyard(data.name);
          setShipyardOpen(true);
        });

        // Listen for tavern interaction from scene
        scene.events.on("tavern:interact", (data: { name: string }) => {
          setDockedIslandName(data.name);
          setTavernOpen(true);
        });

        // Listen for harbor office interaction from scene
        scene.events.on("harboroffice:interact", (data: { name: string }) => {
          console.log("Harbor Office panel opening:", data.name);
          // For now, show it as a message or in research panel
          // You can add a dedicated HarborOfficePanel if needed
          setDockedIslandName(data.name);
          // Placeholder - harbor office functionality can be expanded
        });

        // Listen for ambient trading inventory from scene (React UI)
        scene.events.on(
          "ambient:trade",
          (data: {
            ambientId: string;
            items: AmbientTradeItem[];
            traderName: string;
          }) => {
            setAmbientTradeData({
              ambientId: data.ambientId,
              traderName: data.traderName,
              items: data.items,
            });
            setAmbientTradeOpen(true);
          },
        );

        // Listen for harvest UI events from scene (React UI)
        scene.events.on(
          "harvest:started",
          (data: {
            nodeId: string;
            harvestTime: number;
            nodeName: string;
            requiredSkill?: string;
            requiredLevel?: number;
          }) => {
            setHarvestReward(null);
            playSound(SFX_KEYS.HARVEST_START);
            getSoundManager()?.play(SFX_KEYS.HARVEST_TICK_LOOP);
            if (data.requiredSkill === "mining") {
              setMiningMinigameActive(true);
              setMiningNodeId(data.nodeId);
              setMiningNodeName(data.nodeName);
              setMiningDurationSeconds(data.harvestTime);
              setMiningNodeLevel(data.requiredLevel ?? 1);
              setHarvestProgress(null);
              return;
            }
            setHarvestProgress({
              nodeId: data.nodeId,
              nodeName: data.nodeName,
              harvestTimeSeconds: data.harvestTime,
              startedAt: Date.now(),
            });
          },
        );

        scene.events.on(
          "harvest:complete",
          (data: {
            nodeId: string;
            drops: HarvestDrop[];
            xpGained?: number;
            bonusApplied?: string;
            skillType?: string;
          }) => {
            setHarvestProgress(null);
            getSoundManager()?.stop(SFX_KEYS.HARVEST_TICK_LOOP);
            playSound(SFX_KEYS.HARVEST_COMPLETE);
            setMiningMinigameActive(false);
            setMiningNodeId(null);
            setMiningNodeLevel(1);
            setHarvestReward({
              drops: data.drops || [],
              xpGained: data.xpGained,
              bonusApplied: data.bonusApplied ?? null,
              skillType: data.skillType,
            });
          },
        );

        // Listen for cannon focus minigame events from scene
        scene.events.on("cannonFocus:activate", () => {
          setCannonFocusActive(true);
          scene.setCannonFocusActive(true);
        });

        scene.events.on("cannonFocus:deactivate", () => {
          setCannonFocusActive(false);
          scene.setCannonFocusActive(false);
        });

        scene.events.on(
          "cannonFocus:targetUpdate",
          (data: {
            name: string | null;
            hp: number;
            maxHp: number;
            faction?: string;
            level?: number;
          }) => {
            setCannonFocusTarget({
              name: data.name,
              hp: data.hp,
              maxHp: data.maxHp,
              faction: data.faction,
              level: data.level,
            });
          },
        );

        // Listen for fishing minigame events from scene
        scene.events.on(
          "fishing:minigame:activate",
          (data: { spotType: string; spotId: string }) => {
            setFishingSpotTypeForMinigame(data.spotType);
            setFishingSpotIdForMinigame(data.spotId);
            setFishingMinigameActive(true);
          },
        );

        scene.events.on("fishing:minigame:deactivate", () => {
          setFishingMinigameActive(false);
        });

        scene.events.on("harvest:cancelled", () => {
          setHarvestProgress(null);
          getSoundManager()?.stop(SFX_KEYS.HARVEST_TICK_LOOP);
          setMiningMinigameActive(false);
          setMiningNodeId(null);
          setMiningNodeLevel(1);
        });

        // Listen for ship tooltip events
        scene.events.on(
          "ship:tooltip:show",
          (data: {
            x: number;
            y: number;
            shipData: any;
            shipPosition: { x: number; y: number };
            portraitIndex?: number;
          }) => {
            // Convert screen coordinates to viewport coordinates (account for canvas position on page)
            const canvas = gameRef.current?.canvas;
            let viewportX = data.x;
            let viewportY = data.y;

            if (canvas) {
              const rect = canvas.getBoundingClientRect();
              viewportX = data.x + rect.left;
              viewportY = data.y + rect.top;
            }

            setShipTooltip({
              visible: true,
              x: viewportX,
              y: viewportY,
              shipData: data.shipData,
              shipPosition: data.shipPosition,
              portraitIndex: data.portraitIndex,
            });
          },
        );

        scene.events.on("ship:tooltip:hide", () => {
          setShipTooltip(null);
        });

        // Listen for whirlpool tooltip events
        scene.events.on(
          "whirlpool:tooltip:show",
          (data: WhirlpoolEntryData) => {
            setWhirlpoolTooltip(data);
            setWhirlpoolTooltipVisible(true);
          },
        );

        scene.events.on("whirlpool:tooltip:hide", () => {
          setWhirlpoolTooltipVisible(false);
        });

        // Listen for whirlpool entered event - show HUD
        scene.events.on(
          "whirlpool:entered",
          (data: {
            instance: {
              entryId: string;
              class: "C1" | "C2" | "C3" | "C4" | "C5" | "C6";
              className: string;
              effect: string;
              effectModifiers: {
                armorMultiplier: number;
                shieldMultiplier: number;
                damageMultiplier: number;
                speedMultiplier: number;
                repairMultiplier: number;
                signatureMultiplier: number;
                description: string;
              };
              width: number;
              height: number;
              exitPoint: {
                tileX: number;
                tileY: number;
                worldX: number;
                worldY: number;
                stable: boolean;
              };
              playerCount: number;
              lootMultiplier: number;
              xpMultiplier: number;
            };
            expiresAt?: number;
          }) => {
            setInWhirlpool(true);
            setWhirlpoolInstance(data.instance);
            setWhirlpoolExpiresAt(data.expiresAt);
          },
        );

        // Listen for whirlpool exited event - hide HUD
        scene.events.on("whirlpool:exited", () => {
          setInWhirlpool(false);
          setWhirlpoolInstance(null);
          setWhirlpoolExpiresAt(undefined);
        });

        // Listen for rift entered event
        scene.events.on(
          "rift:entered",
          async (data: {
            riftId: string;
            position: { x: number; y: number };
          }) => {
            setInRift(true);
            setCurrentRiftId(data.riftId);

            // Fetch rift metadata from server
            try {
              const response = await fetch(
                `${apiBaseUrl}/api/rifts/${data.riftId}`,
              );
              if (response.ok) {
                const riftData = await response.json();
                setRiftMetadata({
                  id: riftData.id,
                  name: riftData.name,
                  layer: riftData.layer,
                  anomalyLevel: riftData.anomaly_level || riftData.anomalyLevel,
                  theme: riftData.theme,
                  jumpsToCore: riftData.jumpsToCore,
                });
              }
            } catch (error) {
              console.error("Failed to fetch rift metadata:", error);
            }
          },
        );

        // Listen for rift exited event
        scene.events.on("rift:exited", () => {
          setInRift(false);
          setCurrentRiftId("core");
          setRiftMetadata(null);
        });

        // Listen for sanctum entered event
        scene.events.on(
          "sanctum:entered",
          (data: {
            sanctumId: string;
            number: number;
            type: "survival" | "preservation" | "experiment";
            grade: "A" | "B" | "C" | "D";
          }) => {
            setInSanctum(true);
            setCurrentSanctumId(data.sanctumId);
            setSanctumMetadata({
              id: data.sanctumId,
              number: data.number,
              type: data.type,
              grade: data.grade,
            });
          },
        );

        // Listen for sanctum exited event
        scene.events.on("sanctum:exited", () => {
          setInSanctum(false);
          setCurrentSanctumId(null);
          setSanctumMetadata(null);
        });

        // Listen for construction mode entry from inventory panel
        window.addEventListener(
          "construction:enter_mode",
          handleConstructionEnterMode,
        );

        // Listen for construction mode changes to show/hide hotbar
        const handleConstructionModeChanged = (
          e: Phaser.Events.EventEmitter,
        ) => {
          if (e && (e as any).active !== undefined) {
            setConstructionHotbarActive((e as any).active);
            if ((e as any).active && (e as any).kitId) {
              setCurrentConstructionKitId((e as any).kitId);
            }
          }
        };
        scene.events.on(
          "construction:mode_changed",
          handleConstructionModeChanged,
        );

        // Listen for cooking panel open from inventory button
        const handleCookingOpen = () => {
          setCookingOpen(true);
        };
        window.addEventListener(
          "cooking:open",
          handleCookingOpen as EventListener,
        );

        // Listen for smithing panel open from inventory button
        const handleSmithingOpen = () => {
          setSmithingOpen(true);
        };
        window.addEventListener(
          "smithing:open",
          handleSmithingOpen as EventListener,
        );

        // Listen for alchemy panel open from inventory button
        const handleAlchemyOpen = () => {
          setAlchemyOpen(true);
        };
        window.addEventListener(
          "alchemy:open",
          handleAlchemyOpen as EventListener,
        );

        // Listen for security tier updates
        scene.events.on(
          "security:tier",
          (tier: "safe_zone" | "contested" | "open_waters") => {
            setSecurityTier(tier);
          },
        );

        // Listen for dock proximity events - show dock prompt when in range
        scene.events.on(
          "dock:proximity",
          (data: { islandId: string; islandName: string }) => {
            setDockPrompt({
              islandId: data.islandId,
              islandName: data.islandName,
            });
          },
        );

        scene.events.on("dock:proximityClear", () => {
          setDockPrompt(null);
        });

        // Listen for docking events
        scene.events.on(
          "player:docked",
          (data: {
            islandId: string;
            islandName: string;
            hasShop: boolean;
            hasShipyard: boolean;
            isHarborOffice?: boolean;
            hasTavern?: boolean;
            hasCourier?: boolean;
            hasDockServices?: boolean;
            isPermanent?: boolean;
            npcs?: Array<{
              id: string;
              name: string;
              title: string;
              portrait: string;
              dialogue?: { greeting: string; farewell: string };
            }>;
          }) => {
            setIsDocked(true);
            setDockedIslandId(data.islandId);
            setDockedIslandName(data.islandName);
            setDockedIslandHasShop(data.hasShop);
            setDockPrompt(null); // Auto-hide proximity prompt once docked
            setDockedIslandHasShipyard(data.hasShipyard);
            setDockedIslandIsHarborOffice(data.isHarborOffice || false);
            setDockedIslandHasTavern(data.hasTavern || false);
            setDockedIslandHasCourier(data.hasCourier || false);
            setDockedIslandHasDockServices(data.hasDockServices || false);
            setDockedIslandIsPermanent(data.isPermanent || false);
            setSecurityTier("safe_zone"); // Docked is always safe

            // Store NPCs with quest metadata
            if (data.npcs) {
              const npcsWithQuestStatus = data.npcs.map((npc) => ({
                ...npc,
                questStatus: "none" as const,
                questId: undefined,
              }));
              setDockedIslandNpcs(npcsWithQuestStatus);
            } else {
              setDockedIslandNpcs([]);
            }

            // Emit location_visited event for quest tracking
            socketRef.current?.emit("quest:location_visited", {
              locationId: data.islandId,
            });
          },
        );

        // Handle NPC interaction from DockedPanel
        const handleNPCTalk = (npcId: string, npcName: string) => {
          // Emit quest event to server
          socketRef.current?.emit("quest:npc_interaction", {
            npcId,
            locationId: dockedIslandId,
          });

          // Get quest dialogue if this is a quest NPC
          const bonVoyageQuest = STARTER_VOYAGE_QUESTS.find(
            (q) => q.id === "bon_voyage",
          );
          let questDialogue = undefined;
          let questProgress = 0;

          if (bonVoyageQuest && bonVoyageQuest.mentorDialogue) {
            questDialogue = {
              start: bonVoyageQuest.mentorDialogue.start,
              progress: bonVoyageQuest.mentorDialogue.progress,
              complete: bonVoyageQuest.mentorDialogue.complete,
            };
            // TODO: Get actual quest progress from quest state
            questProgress = 0;
          }

          // Show NPC dialogue modal with quest-aware dialogue
          const npcData = {
            npcId,
            npcName,
            npcTitle: "Merchant",
            npcPortrait: "/assets/portraits/merchants/merchant_1.png",
            dialogue: `Welcome, captain. I'm ${npcName}. What brings you to these waters?`,
            questDialogue,
            questProgress,
          };
          setNpcDialogueData(npcData);
          setNpcDialogueOpen(true);
        };

        // Store handler for use in docking event
        (scene as any).handleNPCTalk = handleNPCTalk;

        scene.events.on("player:undocked", () => {
          closeDockedOverlays();
          setIsDocked(false);
          setDockedIslandId(null);
          setDockedIslandName("Unknown Island");
          setDockedIslandHasShop(false);
          setDockedIslandHasShipyard(false);
          setDockedIslandIsHarborOffice(false);
          setDockedIslandHasTavern(false);
          setDockedIslandHasCourier(false);
          setDockedIslandHasDockServices(false);
          setDockedIslandIsPermanent(false);
          // Recalculate security tier based on current position instead of hardcoding
          const localShip = scene.getLocalShip();
          if (localShip) {
            const tier = scene.calculateSecurityTier(localShip.x, localShip.y);
            setSecurityTier(tier);
          }
        });

        // Listen for rift storm events
        scene.events.on(
          "riftStorm:spawned",
          (data: {
            id: string;
            x: number;
            y: number;
            radius: number;
            tier: string;
            color: string;
            expiresAt: number;
          }) => {
            setRiftStorms((prev) => {
              // Add if not already present
              if (prev.some((s) => s.id === data.id)) return prev;
              return [
                ...prev,
                {
                  id: data.id,
                  x: data.x,
                  y: data.y,
                  radius: data.radius,
                  tier: data.tier,
                  color: data.color,
                  expiresAt: data.expiresAt,
                },
              ];
            });
          },
        );

        scene.events.on("riftStorm:despawned", (data: { id: string }) => {
          setRiftStorms((prev) => prev.filter((s) => s.id !== data.id));
        });

        // Listen for pirate raid events (for world map)
        scene.events.on("raid:list", (raids: PirateRaidData[]) => {
          setPirateRaids(raids);
        });

        scene.events.on(
          "raid:started",
          (data: {
            id: string;
            islandId: string;
            islandName: string;
            islandX: number;
            islandY: number;
            tier: string;
            expiresAt: number;
            pirateCount: number;
            rewardPool: number;
          }) => {
            setPirateRaids((prev) => {
              if (prev.some((r) => r.id === data.id)) return prev;
              return [
                ...prev,
                {
                  id: data.id,
                  islandId: data.islandId,
                  islandName: data.islandName,
                  islandX: data.islandX,
                  islandY: data.islandY,
                  tier: data.tier,
                  expiresAt: data.expiresAt,
                  piratesDefeated: 0,
                  totalPirates: data.pirateCount,
                  rewardPool: data.rewardPool,
                },
              ];
            });
          },
        );

        scene.events.on(
          "raid:progress",
          (data: {
            id: string;
            piratesDefeated: number;
            totalPirates: number;
            participantCount: number;
          }) => {
            setPirateRaids((prev) =>
              prev.map((r) =>
                r.id === data.id
                  ? {
                      ...r,
                      piratesDefeated: data.piratesDefeated,
                      totalPirates: data.totalPirates,
                    }
                  : r,
              ),
            );
          },
        );

        scene.events.on(
          "raid:ended",
          (data: { id: string; success: boolean; islandName: string }) => {
            setPirateRaids((prev) => prev.filter((r) => r.id !== data.id));
          },
        );

        scene.events.on(
          "riftStorm:entered",
          (data: {
            id: string;
            tier: string;
            lootMultiplier: number;
            xpMultiplier: number;
          }) => {
            setInRiftStorm(true);
            setRiftStormBonus({
              loot: data.lootMultiplier,
              xp: data.xpMultiplier,
            });
            setCurrentRiftStormId(data.id);
            setCurrentRiftStormTier(data.tier);
            setCurrentRiftStormLootMultiplier(data.lootMultiplier);
            setCurrentRiftStormXpMultiplier(data.xpMultiplier);
          },
        );

        scene.events.on("riftStorm:exited", () => {
          setInRiftStorm(false);
          setRiftStormBonus(null);
        });

        // Fetch rift scar data from server API
        const fetchRiftScars = async (): Promise<void> => {
          try {
            const response = await fetch(`${apiBaseUrl}/api/rift-scars`);
            if (response.ok) {
              const scars = await response.json();
              console.log("[RIFT SCARS] Fetched", scars.length, "rift scars");
              setRiftScars(
                scars.map((scar: any) => ({
                  id: scar.id,
                  x: scar.worldX,
                  y: scar.worldY,
                  name: scar.name,
                })),
              );
            }
          } catch (error) {
            console.error("[RIFT SCARS] Failed to fetch rift scars:", error);
          }
        };
        fetchRiftScars();

        // Listen for context menu events from game world interactions
        scene.events.on(
          "island:research",
          (data: {
            islandId: string;
            islandPosition: { x: number; y: number };
            screenPosition: { x: number; y: number };
            canDock: boolean;
            isDocked: boolean;
            isDockedHere: boolean;
            isPermanent?: boolean;
            permanentName?: string;
          }) => {
            console.log(
              `Island research event received! screenPos: (${data.screenPosition.x}, ${data.screenPosition.y}), isPermanent: ${data.isPermanent}`,
            );
            contextMenuEmitter.show(
              {
                type: "island",
                data: {
                  islandId: data.islandId,
                  islandPosition: data.islandPosition,
                  canDock: data.canDock,
                  isDocked: data.isDocked,
                  isDockedHere: data.isDockedHere,
                  isPermanent: data.isPermanent,
                  permanentName: data.permanentName,
                },
              },
              data.screenPosition.x,
              data.screenPosition.y,
            );
          },
        );

        // Listen for research panel open event from context menu
        scene.events.on(
          "island:openResearch",
          (data: {
            islandId: string;
            islandPosition: { x: number; y: number };
          }) => {
            console.log(
              `[RESEARCH] Opening research panel for island: ${data.islandId}`,
            );
            setResearchIslandId(data.islandId);
            setResearchIslandPosition(data.islandPosition);
            setResearchPanelOpen(true);
          },
        );

        scene.events.on(
          "resource:contextmenu",
          (data: {
            nodeId: string;
            nodeType: string;
            nodeName: string;
            worldX: number;
            worldY: number;
            screenX: number;
            screenY: number;
          }) => {
            contextMenuEmitter.show(
              {
                type: "resource",
                data: {
                  resourceNodeId: data.nodeId,
                  resourceNodeType: data.nodeType,
                  resourceNodeName: data.nodeName,
                  resourceNodePosition: { x: data.worldX, y: data.worldY },
                },
              },
              data.screenX,
              data.screenY,
            );
          },
        );

        scene.events.on(
          "riftscar:contextmenu",
          (data: { scarData: any; screenX: number; screenY: number }) => {
            console.log(
              `Rift scar context menu event received for: ${data.scarData.name}`,
            );
            contextMenuEmitter.show(
              {
                type: "riftscar",
                data: {
                  scarData: data.scarData,
                },
              },
              data.screenX,
              data.screenY,
            );
          },
        );

        scene.events.on("riftscar:openResearch", (data: { scarData: any }) => {
          console.log(
            `[RESEARCH] Opening rift scar research panel: ${data.scarData.name}`,
          );
          setRiftScarData(data.scarData);
        });

        scene.events.on(
          "resource:openResearch",
          (data: {
            nodeId: string;
            nodeType: string;
            nodePosition: { x: number; y: number };
          }) => {
            console.log(
              `GameCanvas: Received resource:openResearch event, opening research panel for ${data.nodeId}`,
            );
            setResourceNodeId(data.nodeId);
            setResourceNodeType(data.nodeType);
            setResourceNodePosition(data.nodePosition);
            setResourceResearchOpen(true);
          },
        );

        scene.events.on(
          "fishingspot:research",
          (data: {
            spotId: string;
            spotType: string;
            spotPosition: { x: number; y: number };
            tilePosition: { x: number; y: number };
            screenPosition: { x: number; y: number };
          }) => {
            contextMenuEmitter.show(
              {
                type: "fishing",
                data: {
                  spotId: data.spotId,
                  spotType: data.spotType,
                  spotPosition: data.spotPosition,
                  tileX: data.tilePosition.x,
                  tileY: data.tilePosition.y,
                },
              },
              data.screenPosition.x,
              data.screenPosition.y,
            );
          },
        );

        scene.events.on(
          "shipwreck:contextmenu",
          (data: {
            wreckId: string;
            worldX: number;
            worldY: number;
            screenX: number;
            screenY: number;
            lootQuality?: string;
            isSanctumContainer?: boolean;
            containerData?: any;
          }) => {
            // Check if this is a sanctum container and show appropriate context menu
            if (data.isSanctumContainer) {
              contextMenuEmitter.show(
                {
                  type: "sanctum_container",
                  data: {
                    containerId: data.wreckId,
                    containerData: data.containerData,
                  },
                },
                data.screenX,
                data.screenY,
              );
            } else {
              contextMenuEmitter.show(
                {
                  type: "debris",
                  data: {
                    containerId: data.wreckId,
                    lootQuality: data.lootQuality,
                  },
                },
                data.screenX,
                data.screenY,
              );
            }
          },
        );

        scene.events.on(
          "whirlpool:contextmenu",
          (data: {
            whirlpoolId: string;
            whirlpoolClass: string;
            worldX: number;
            worldY: number;
            screenX: number;
            screenY: number;
          }) => {
            contextMenuEmitter.show(
              {
                type: "whirlpool",
                data: {
                  whirlpoolId: data.whirlpoolId,
                  whirlpoolClass: data.whirlpoolClass,
                },
              },
              data.screenX,
              data.screenY,
            );
          },
        );

        scene.events.on(
          "ship:contextmenu",
          (data: {
            shipId: string;
            screenX: number;
            screenY: number;
            shipData: any;
          }) => {
            console.log(
              `GameCanvas: Received ship:contextmenu event, showing context menu at (${data.screenX}, ${data.screenY})`,
            );
            contextMenuEmitter.show(
              {
                type: "ship",
                data: {
                  shipId: data.shipId,
                  shipData: data.shipData,
                },
              },
              data.screenX,
              data.screenY,
            );
          },
        );

        scene.events.on(
          "sanctum:container:contextmenu",
          (data: {
            containerId: string;
            containerData: any;
            screenX: number;
            screenY: number;
          }) => {
            console.log(
              `GameCanvas: Received sanctum:container:contextmenu event for ${data.containerId}`,
            );
            contextMenuEmitter.show(
              {
                type: "sanctum_container",
                data: {
                  containerId: data.containerId,
                  containerData: data.containerData,
                },
              },
              data.screenX,
              data.screenY,
            );
          },
        );

        scene.events.on(
          "ship:openInvestigation",
          (data: { shipId: string; shipData: any }) => {
            console.log(
              `GameCanvas: Received ship:openInvestigation event, opening investigation panel for ${data.shipId}`,
            );
            const shipData = data.shipData;
            setInvestigatedShip({
              shipId: data.shipId,
              name: shipData?.name,
              ownerId: shipData?.ownerId,
              ownerName: shipData?.ownerName,
              fleetTag: shipData?.fleetTag || null,
              titleName: shipData?.titleName || null,
              isNPC: shipData?.ownerType === "npc",
              level: shipData?.level,
              hp: shipData?.hullHp,
              maxHp: shipData?.maxHullHp,
              faction: shipData?.faction,
              subfaction: shipData?.subfaction,
              defense: shipData?.defense,
              damage: shipData?.damage,
            });
            setShipInvestigationOpen(true);
          },
        );

        scene.events.on(
          "world:contextmenu",
          (data: {
            worldX: number;
            worldY: number;
            screenX: number;
            screenY: number;
          }) => {
            console.log(
              `GameCanvas: Received world:contextmenu event, showing context menu at (${data.screenX}, ${data.screenY})`,
            );
            contextMenuEmitter.show(
              {
                type: "world",
                data: {
                  worldX: data.worldX,
                  worldY: data.worldY,
                },
              },
              data.screenX,
              data.screenY,
            );
          },
        );
      }
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener(
        "construction:enter_mode",
        handleConstructionEnterMode as EventListener,
      );
      if (canvas) {
        canvas.removeEventListener(
          "webglcontextlost",
          handleContextLost as EventListener,
        );
        canvas.removeEventListener(
          "webglcontextrestored",
          handleContextRestored as EventListener,
        );
      }
      gameRef.current?.destroy(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Setters from useState are stable and don't need to be in dependencies
  }, [onSetDestination, closeDockedOverlays, callReadyIfPossible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasEssenceEquipped && signaturePanelOpen) {
      setSignaturePanelOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEssenceEquipped, signaturePanelOpen, setSignaturePanelOpen]);

  const currentRegionData = REGIONS.find((r) => r.id === currentRegion);

  const securityLabel = (
    tier: "safe_zone" | "contested" | "open_waters",
    isDocked: boolean = false,
    inSanctum: boolean = false,
    inWhirlpool: boolean = false,
  ): string => {
    if (isDocked) return "SAFE: DOCKED";
    // In rifts, show rift-specific security based on anomaly level
    if (inRift && riftMetadata) {
      const level = riftMetadata.anomalyLevel || 1;
      if (level <= 2) return "RIFT: LOW";
      if (level <= 4) return "RIFT: MODERATE";
      if (level <= 6) return "RIFT: HIGH";
      return "RIFT: EXTREME";
    }
    if (inSanctum) return "SANCTUM";
    if (inWhirlpool) return "WHIRLPOOL";
    if (inRift) return "RIFT: UNKNOWN";
    if (tier === "safe_zone") return "SAFE";
    if (tier === "contested") return "AT RISK: NAVY COVER";
    return "MAX RISK: OPEN WATERS";
  };

  const getSpaceLabel = (
    isInWhirlpool: boolean,
    isInRift: boolean,
    riftName?: string,
    riftAnomalyLevel?: number,
    jumpsToCore?: number,
  ): string => {
    if (isInWhirlpool) return "WHIRLPOOL";
    if (inSanctum && sanctumMetadata) {
      return `SANCTUM ${sanctumMetadata.number} [${sanctumMetadata.type.toUpperCase()}]`;
    }
    if (isInRift) {
      if (riftName) {
        const anomalyLabel = riftAnomalyLevel ? ` [LV${riftAnomalyLevel}]` : "";
        const jumpsLabel =
          jumpsToCore !== undefined && jumpsToCore > 0
            ? ` (${jumpsToCore} ${jumpsToCore === 1 ? "jump" : "jumps"} from core)`
            : "";
        return `RIFT: ${riftName.toUpperCase()}${anomalyLabel}${jumpsLabel}`;
      }
      // Fallback to riftId if metadata not loaded yet
      return `RIFT: ${currentRiftId?.toUpperCase() || "UNKNOWN"}`;
    }
    return "WORLD";
  };

  const getWeatherLabel = (
    weather: "clear" | "fog" | "storm" | "calm",
  ): string => {
    const labels: Record<string, string> = {
      clear: "CLEAR",
      fog: "FOG",
      storm: "STORM",
      calm: "CALM",
    };
    return labels[weather] || "CLEAR";
  };

  const cancelHarvest = useCallback((): void => {
    if (!harvestProgress) return;
    socketRef.current?.emit("resource:cancelHarvest", {
      nodeId: harvestProgress.nodeId,
    });
    setHarvestProgress(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [harvestProgress, setHarvestProgress]);

  const cancelMiningHarvest = useCallback((): void => {
    if (!miningNodeId) return;
    socketRef.current?.emit("resource:cancelHarvest", { nodeId: miningNodeId });
    setMiningMinigameActive(false);
    setMiningNodeId(null);
    setMiningNodeLevel(1);
  }, [miningNodeId]);

  const failMiningHarvest = useCallback((): void => {
    if (!miningNodeId) return;
    socketRef.current?.emit("resource:failHarvest", { nodeId: miningNodeId });
    setMiningMinigameActive(false);
    setMiningNodeId(null);
    setMiningNodeLevel(1);
  }, [miningNodeId]);

  const completeHarvest = useCallback((): void => {
    if (!harvestProgress) return;
    socketRef.current?.emit("resource:completeHarvest", {
      nodeId: harvestProgress.nodeId,
    });
    setHarvestProgress(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [harvestProgress, setHarvestProgress]);

  const completeMiningHarvest = useCallback((): void => {
    if (!miningNodeId) return;
    socketRef.current?.emit("resource:completeHarvest", {
      nodeId: miningNodeId,
    });
    setMiningMinigameActive(false);
    setMiningNodeId(null);
    setMiningNodeLevel(1);
  }, [miningNodeId]);

  const getInfamyTierLabel = (value: number): string => {
    if (value >= 1_000_000) return "Emperor of the Sea";
    if (value >= 500_000) return "Warlord";
    if (value >= 100_000) return "Dread Captain";
    if (value >= 50_000) return "Scourge";
    if (value >= 10_000) return "Marauder";
    if (value >= 1_000) return "Raider";
    return "Deckhand";
  };

  // XP needed for next level - mirrors server-side ShipProgression.xpForLevel(level + 1)
  const xpNeededForNext = 100 * (level + 1) + 50 * (level + 1) * level;

  return (
    <GlobalTooltipProvider>
      <div className="game-layout">
        {kickMessage && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              zIndex: 10000,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontFamily: "var(--font-mono), monospace",
            }}
          >
            <div
              style={{
                width: 420,
                maxWidth: "90vw",
                border: "2px solid #30363d",
                backgroundColor: "#0d1117",
                padding: 16,
                borderRadius: 0,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#e6edf3",
                  marginBottom: 8,
                }}
              >
                DISCONNECTED
              </div>
              <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 12 }}>
                {kickMessage}
              </div>
              <button
                type="button"
                onClick={() => setKickMessage(null)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  backgroundColor: "#21262d",
                  border: "1px solid #30363d",
                  color: "#e6edf3",
                  cursor: "pointer",
                  borderRadius: 0,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Toast Notifications (queue) */}
        <div
          style={{
            position: "fixed",
            bottom: 20,
            left: 20,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            zIndex: 5000,
            maxWidth: 320,
            fontFamily: "var(--font-mono)",
          }}
        >
          {toastQueue.map((toast) => (
            <div
              key={toast.id}
              role="button"
              tabIndex={0}
              onClick={() => removeToast(toast.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  removeToast(toast.id);
                }
              }}
              style={{
                backgroundColor:
                  toast.type === "error"
                    ? "#da3633"
                    : toast.type === "warning"
                      ? "#f0883e"
                      : "#238636",
                border: `1px solid ${toast.type === "error" ? "#8b3a3a" : toast.type === "warning" ? "#a85a2a" : "#2ea043"}`,
                color: "#ffffff",
                padding: "12px 16px",
                borderRadius: 0,
                fontSize: 12,
                fontWeight: 700,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
                cursor: "pointer",
              }}
            >
              {toast.text}
            </div>
          ))}
        </div>

        {/* World Event Notifications (top-right) */}
        {worldEventNotificationsEnabled && (
          <WorldEventNotification
            events={worldEvents}
            onRemove={removeWorldEvent}
          />
        )}

        {/* Server Feedback Panel (bottom-left for debugging) */}
        <ServerFeedbackPanel
          socket={socketRef.current}
          isEnabled={serverFeedbackEnabled}
        />

        {/* Combat Cooldown Tracker (left side during combat) */}
        <CombatCooldownTracker
          cooldowns={combatCooldowns}
          isInCombat={inCombat}
        />

        {/* Compact Sidebar */}
        <aside className="game-sidebar">
          {/* Player Header */}
          <div className="sidebar-header">
            <div className="player-info-compact">
              <div className="player-info-details">
                <span className="player-name">{playerInfo.name}</span>
                <span className="player-ship-type">
                  {(shipType === "raft" ? "barrel" : shipType)
                    .replace("_", " ")
                    .toUpperCase()}
                </span>
              </div>
              <span className="player-level">Lv.{level}</span>
            </div>
            <div className="xp-bar-sidebar">
              <div
                className="xp-bar-fill-sidebar"
                style={{
                  width: `${Math.min(100, xpNeededForNext > 0 ? (xp / xpNeededForNext) * 100 : 0)}%`,
                }}
              />
            </div>
            <div className="xp-text-sidebar">
              {xp} / {xpNeededForNext}
            </div>
            <div className="berry-display-sidebar">
              <span className="berry-icon-small">₱</span>
              <span className="berry-amount-small">
                {pieces.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Ship Status - Compact (HP/SP at top under header) */}
          <div className="sidebar-section compact">
            <div className="section-header">
              <span>SHIP</span>
              {inCombat && <span className="combat-badge">COMBAT</span>}
            </div>
            <div className="hp-bars-compact">
              <div className="hp-row">
                <span className="hp-label">HP</span>
                <div className="hp-bar-mini">
                  <div
                    className="hp-bar-fill-mini"
                    style={{
                      width: `${(playerHp.hull / playerHp.maxHull) * 100}%`,
                      backgroundColor:
                        playerHp.hull / playerHp.maxHull > 0.5
                          ? "#27ae60"
                          : playerHp.hull / playerHp.maxHull > 0.25
                            ? "#f39c12"
                            : "#e74c3c",
                    }}
                  />
                </div>
                <span className="hp-value">{playerHp.hull}</span>
              </div>
              <div className="hp-row">
                <span className="hp-label">SP</span>
                <div className="hp-bar-mini">
                  <div
                    className="hp-bar-fill-mini"
                    style={{
                      width: `${(playerSP.current / playerSP.max) * 100}%`,
                      backgroundColor:
                        playerSP.current / playerSP.max > 0.75
                          ? "#3498db"
                          : playerSP.current / playerSP.max > 0.25
                            ? "#f39c12"
                            : "#e74c3c",
                    }}
                  />
                </div>
                <span className="hp-value">{playerSP.current}</span>
              </div>
            </div>
          </div>

          {/* Infamy */}
          <div className="sidebar-section compact">
            <div className="section-header">
              <span>INFAMY</span>
              <button
                className="info-button"
                onClick={() => setInfamyInfoOpen(true)}
                title="Learn about the Infamy system"
              >
                ?
              </button>
            </div>
            <div className="infamy-display-sidebar">
              <div className="infamy-row">
                <div className="infamy-text">
                  <span className="infamy-tier">
                    {getInfamyTierLabel(infamy)}
                  </span>
                  <span className="infamy-value">
                    {infamy.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Faction Reputation */}
          <div className="sidebar-section compact">
            <div className="section-header">
              <span>REPUTATION</span>
              <button
                className="info-button"
                type="button"
                onClick={() => setReputationOpen((prev) => !prev)}
                title={
                  reputationOpen
                    ? "Hide faction reputation"
                    : "Show faction reputation"
                }
              >
                {reputationOpen ? "-" : "+"}
              </button>
            </div>
            {reputationOpen && (
              <div className="faction-reputation">
                {/* Navy */}
                <div className="faction-row">
                  <span className="faction-icon">[N]</span>
                  <span className="faction-name">Navy</span>
                  <span
                    className="faction-tier"
                    style={{ color: factionTiers.navy.color }}
                  >
                    {factionTiers.navy.name}
                  </span>
                </div>
                <div className="faction-bar-row">
                  <div className="faction-bar">
                    <div
                      className="faction-bar-fill"
                      style={{
                        width: `${Math.min(100, Math.abs(factionReputation.navy) / 100)}%`,
                        backgroundColor:
                          factionReputation.navy >= 0 ? "#3498db" : "#e74c3c",
                        marginLeft:
                          factionReputation.navy < 0
                            ? `${50 - Math.min(50, Math.abs(factionReputation.navy) / 200)}%`
                            : "50%",
                      }}
                    />
                    <div className="faction-bar-center" />
                  </div>
                  <span
                    className={`faction-value ${factionReputation.navy >= 0 ? "positive" : "negative"}`}
                  >
                    {factionReputation.navy >= 0 ? "+" : ""}
                    {factionReputation.navy}
                  </span>
                </div>
                {/* Pirates */}
                <div className="faction-row">
                  <span className="faction-icon">[P]</span>
                  <span className="faction-name">Pirates</span>
                  <span
                    className="faction-tier"
                    style={{ color: factionTiers.pirate.color }}
                  >
                    {factionTiers.pirate.name}
                  </span>
                </div>
                <div className="faction-bar-row">
                  <div className="faction-bar">
                    <div
                      className="faction-bar-fill"
                      style={{
                        width: `${Math.min(100, Math.abs(factionReputation.pirate) / 100)}%`,
                        backgroundColor:
                          factionReputation.pirate >= 0 ? "#e74c3c" : "#e74c3c",
                        marginLeft:
                          factionReputation.pirate < 0
                            ? `${50 - Math.min(50, Math.abs(factionReputation.pirate) / 200)}%`
                            : "50%",
                      }}
                    />
                    <div className="faction-bar-center" />
                  </div>
                  <span
                    className={`faction-value ${factionReputation.pirate >= 0 ? "positive" : "negative"}`}
                  >
                    {factionReputation.pirate >= 0 ? "+" : ""}
                    {factionReputation.pirate}
                  </span>
                </div>
                {/* Merchants */}
                <div className="faction-row">
                  <span className="faction-icon">[M]</span>
                  <span className="faction-name">Merchants</span>
                  <span
                    className="faction-tier"
                    style={{ color: factionTiers.merchant.color }}
                  >
                    {factionTiers.merchant.name}
                  </span>
                </div>
                <div className="faction-bar-row">
                  <div className="faction-bar">
                    <div
                      className="faction-bar-fill"
                      style={{
                        width: `${Math.min(100, Math.abs(factionReputation.merchant) / 100)}%`,
                        backgroundColor:
                          factionReputation.merchant >= 0
                            ? "#f39c12"
                            : "#e74c3c",
                        marginLeft:
                          factionReputation.merchant < 0
                            ? `${50 - Math.min(50, Math.abs(factionReputation.merchant) / 200)}%`
                            : "50%",
                      }}
                    />
                    <div className="faction-bar-center" />
                  </div>
                  <span
                    className={`faction-value ${factionReputation.merchant >= 0 ? "positive" : "negative"}`}
                  >
                    {factionReputation.merchant >= 0 ? "+" : ""}
                    {factionReputation.merchant}
                  </span>
                </div>
                {/* Smugglers */}
                <div className="faction-row">
                  <span className="faction-icon">[S]</span>
                  <span className="faction-name">Smugglers</span>
                  <span
                    className="faction-tier"
                    style={{ color: factionTiers.smuggler.color }}
                  >
                    {factionTiers.smuggler.name}
                  </span>
                </div>
                <div className="faction-bar-row">
                  <div className="faction-bar">
                    <div
                      className="faction-bar-fill"
                      style={{
                        width: `${Math.min(100, Math.abs(factionReputation.smuggler) / 100)}%`,
                        backgroundColor:
                          factionReputation.smuggler >= 0
                            ? "#9b59b6"
                            : "#e74c3c",
                        marginLeft:
                          factionReputation.smuggler < 0
                            ? `${50 - Math.min(50, Math.abs(factionReputation.smuggler) / 200)}%`
                            : "50%",
                      }}
                    />
                    <div className="faction-bar-center" />
                  </div>
                  <span
                    className={`faction-value ${factionReputation.smuggler >= 0 ? "positive" : "negative"}`}
                  >
                    {factionReputation.smuggler >= 0 ? "+" : ""}
                    {factionReputation.smuggler}
                  </span>
                </div>
                {/* Daily cap info */}
                <div className="faction-hint">
                  <span style={{ color: "#8b949e", fontSize: "9px" }}>
                    Daily:{" "}
                    {factionDailyGains.navy +
                      factionDailyGains.pirate +
                      factionDailyGains.merchant +
                      factionDailyGains.smuggler}{" "}
                    / {factionDailyCaps.navy} earned
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Location - Compact */}
          <div className="sidebar-section compact">
            <div className="section-header">LOCATION</div>
            <div className="location-compact">
              {inWhirlpool ? (
                <>
                  <span
                    className="region-badge"
                    style={{ backgroundColor: "#9b59b6" }}
                  >
                    WHIRLPOOL
                  </span>
                  <span className="coords">
                    [{tilePos.x}, {tilePos.y}]
                  </span>
                </>
              ) : inRift && riftMetadata ? (
                <>
                  <span
                    className="region-badge"
                    style={{ backgroundColor: "#e74c3c" }}
                  >
                    {riftMetadata.name.toUpperCase()}
                  </span>
                  <span className="coords" style={{ color: "#ff6b6b" }}>
                    LAYER {riftMetadata.layer} • LV{riftMetadata.anomalyLevel}
                  </span>
                </>
              ) : inSanctum && sanctumMetadata ? (
                <>
                  <span
                    className="region-badge"
                    style={{ backgroundColor: "#3498db" }}
                  >
                    SANCTUM {sanctumMetadata.number}
                  </span>
                  <span className="coords" style={{ color: "#3498db" }}>
                    {sanctumMetadata.type.toUpperCase()} • GRADE{" "}
                    {sanctumMetadata.grade}
                  </span>
                </>
              ) : (
                <>
                  <span
                    className="region-badge"
                    style={{ backgroundColor: currentRegionData?.color }}
                  >
                    {currentRegionData?.name}
                  </span>
                  <span className="coords">
                    [{tilePos.x}, {tilePos.y}]
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Mini Map */}
          <div className="sidebar-section compact">
            <div className="section-header">MAP</div>
            {inWhirlpool || inRift || inSanctum ? (
              <div
                className="mini-map"
                style={{ opacity: 0.5, position: "relative" }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "#8b949e",
                    fontSize: "10px",
                    textAlign: "center",
                    fontWeight: "bold",
                    whiteSpace: "pre",
                  }}
                >
                  {inWhirlpool
                    ? "WHIRLPOOL\nINSTANCE"
                    : inRift
                      ? "RIFT\nINSTANCE"
                      : "SANCTUM\nINSTANCE"}
                </div>
                {Array.from({ length: WORLD_CHUNKS }, (_, row) => (
                  <div key={row} className="map-row">
                    {Array.from({ length: WORLD_CHUNKS }, (_, col) => (
                      <div
                        key={col}
                        className="map-cell"
                        style={{ backgroundColor: "#1a1a1a" }}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mini-map">
                {Array.from({ length: WORLD_CHUNKS }, (_, row) => (
                  <div key={row} className="map-row">
                    {Array.from({ length: WORLD_CHUNKS }, (_, col) => {
                      const isCurrentChunk =
                        col === chunkPos.x && row === chunkPos.y;
                      const region = getChunkRegion(col, row);
                      return (
                        <div
                          key={col}
                          className={`map-cell ${isCurrentChunk ? "current" : ""}`}
                          style={{
                            backgroundColor: region?.color || "#2d3748",
                          }}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="sidebar-section compact">
            <div className="more-menu-container" ref={sidebarMenuRef}>
              <button
                onClick={() => setSidebarMenuOpen(!sidebarMenuOpen)}
                className="action-btn more-btn"
                title="Show menu options"
              >
                MENU ▼
              </button>
              {sidebarMenuOpen && (
                <div className="more-menu-dropdown">
                  <button
                    onClick={() => {
                      setInventoryOpen(true);
                      setSidebarMenuOpen(false);
                    }}
                    className="more-menu-item"
                  >
                    Inventory
                  </button>
                  <button
                    onClick={() => {
                      setQuestLogOpen(true);
                      setSidebarMenuOpen(false);
                    }}
                    className="more-menu-item"
                  >
                    Quests
                  </button>
                  <button
                    onClick={() => {
                      if (inRift) {
                        setRiftNavigatorOpen(true);
                        enqueueToast(
                          "Use the Rift Navigator to view your location in rifts",
                          "warning",
                        );
                      } else {
                        onOpenWorldMap(position);
                      }
                      setSidebarMenuOpen(false);
                    }}
                    className="more-menu-item"
                  >
                    {inRift ? "Rift Map" : "World Map"}
                  </button>
                  <button
                    onClick={() => {
                      onOpenFactionWarfare?.();
                      setSidebarMenuOpen(false);
                    }}
                    className="more-menu-item"
                  >
                    Faction Warfare
                  </button>
                  <button
                    onClick={() => {
                      territoryOverviewEmitter.emit();
                      setSidebarMenuOpen(false);
                    }}
                    className="more-menu-item"
                  >
                    Territories
                  </button>
                  <button
                    onClick={() => {
                      setWorldHistoryOpen(true);
                      setSidebarMenuOpen(false);
                    }}
                    className="more-menu-item"
                  >
                    Legends
                  </button>
                  <button
                    onClick={() => {
                      setOptionsOpen(true);
                      setSidebarMenuOpen(false);
                    }}
                    className="more-menu-item"
                  >
                    Options
                  </button>
                  <button
                    onClick={() => {
                      setFeedbackModalOpen(true);
                      setSidebarMenuOpen(false);
                    }}
                    className="more-menu-item"
                  >
                    Feedback
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="sidebar-footer-compact">
            <span
              className={`status-indicator ${connected ? "online" : "offline"}`}
            />
            <span className="status-text">{shipCount} ships nearby</span>
            <a
              href="https://discord.com/invite/tvbEDUZvvB"
              target="_blank"
              rel="noopener noreferrer"
              className="discord-link"
              title="Join our Discord"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 0-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .078.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
              </svg>
            </a>
          </div>
        </aside>

        {/* Main Game Area */}
        <main
          className="game-main"
          onClick={(e) => {
            // Only hide context menu if click didn't come from the context menu itself
            const target = e.target as HTMLElement;
            if (!target.closest(".global-context-menu")) {
              contextMenuEmitter.hide();
            }
          }}
        >
          <div className="site-hud">
            <div className={`weather-status-bar weather-${currentWeather}`}>
              <span className="font-mono">
                {getWeatherLabel(currentWeather)}
              </span>
            </div>
            <div className={`security-status-bar ${securityTier}`}>
              <span className="font-mono">
                {securityLabel(securityTier, isDocked, inSanctum, inWhirlpool)}
              </span>
            </div>
            <div
              className={`space-status-bar ${inWhirlpool ? "whirlpool" : inRift ? "rift" : "world"}`}
            >
              <span className="font-mono">
                {getSpaceLabel(
                  inWhirlpool,
                  inRift,
                  riftMetadata?.name,
                  riftMetadata?.anomalyLevel,
                  riftMetadata?.jumpsToCore,
                )}
              </span>
            </div>
          </div>

          <div className="game-hud-right">
            <GameMinimap
              gameRef={gameRef}
              playerPosition={position}
              destination={destination}
            />
            <GameCompass
              playerPosition={position}
              destination={destination}
              onClear={onClearDestination}
            />
          </div>

          <div className="game-canvas-container">
            <div className="relative">
              <div id="game-canvas" />

              <HarvestProgressOverlay
                isOpen={!!harvestProgress}
                nodeName={harvestProgress?.nodeName || "Resource"}
                startedAt={harvestProgress?.startedAt || Date.now()}
                harvestTimeSeconds={harvestProgress?.harvestTimeSeconds || 0}
                onCancel={cancelHarvest}
                onComplete={completeHarvest}
              />

              <HarvestRewardToast
                isOpen={!!harvestReward}
                drops={harvestReward?.drops || []}
                xpGained={harvestReward?.xpGained ?? undefined}
                bonusApplied={harvestReward?.bonusApplied ?? undefined}
                skillType={harvestReward?.skillType}
                onClose={() => setHarvestReward(null)}
              />
            </div>

            {/* Directional Indicators - N/S/E/W triangles (nautical colors) */}
            <div className="direction-indicator north" title="North">
              <div className="direction-arrow" />
            </div>
            <div className="direction-indicator south" title="South">
              <div className="direction-arrow" />
            </div>
            <div className="direction-indicator east" title="East">
              <div className="direction-arrow" />
            </div>
            <div className="direction-indicator west" title="West">
              <div className="direction-arrow" />
            </div>
          </div>

          {/* Chat Input - Appears at bottom of game canvas when Enter is pressed */}
          {chatOpen && (
            <div className="chat-input-container">
              <input
                type="text"
                className="chat-input"
                placeholder="Type a message..."
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyDown={(e) => {
                  // Prevent game-level keybinds (including SPACE) from intercepting input.
                  e.stopPropagation();

                  if (e.key === "Enter" && chatMessage.trim()) {
                    e.preventDefault();
                    socketRef.current?.emit("chat:send", {
                      message: chatMessage.trim(),
                    });
                    setChatMessage("");
                    setChatOpen(false);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setChatMessage("");
                    setChatOpen(false);
                  }
                }}
                autoFocus
                maxLength={200}
              />
              <span className="chat-hint">[Enter] Send | [Esc] Cancel</span>
            </div>
          )}

          {/* Hotbar */}
          <div style={{ display: hotbarVisible ? "block" : "none" }}>
            <Hotbar
              config={hotbarConfig}
              onSlotUpdate={updateSlot}
              onSlotUse={useSlot}
              inventory={{ items: inventoryItems }}
            />
          </div>

          {/* Instructions Footer */}
          <footer className="game-footer">
            {/* <span className="instruction">Left-click: Move/Attack</span> */}
            {/* <span className="separator">|</span> */}
            {/* <span className="instruction">Right-click: Options</span> */}
            {/* <span className="separator">|</span> */}
            <button
              type="button"
              className="instruction-btn"
              onClick={() => {
                if (!inventoryOpen && !shopOpen && !skillsOpen) {
                  setInventoryOpen(true);
                }
              }}
            >
              [I]nventory
            </button>
            <span className="separator">|</span>
            <button
              type="button"
              className="instruction-btn"
              onClick={() => {
                if (
                  !inventoryOpen &&
                  !shopOpen &&
                  !skillsOpen &&
                  !fittingOpen &&
                  !craftingOpen
                ) {
                  setSkillsOpen(true);
                }
              }}
            >
              [K] Skills
            </button>
            <span className="separator">|</span>
            <button
              type="button"
              className="instruction-btn"
              onClick={() => {
                if (
                  !inventoryOpen &&
                  !shopOpen &&
                  !skillsOpen &&
                  !fittingOpen &&
                  !craftingOpen
                ) {
                  setFittingOpen(true);
                }
              }}
            >
              [F] Ship
            </button>
            <span className="separator">|</span>
            <button
              type="button"
              className="instruction-btn"
              onClick={() => {
                if (
                  !inventoryOpen &&
                  !shopOpen &&
                  !skillsOpen &&
                  !fittingOpen &&
                  !craftingOpen
                ) {
                  setCraftingOpen(true);
                }
              }}
            >
              [C]raft
            </button>
            <span className="separator">|</span>
            <button
              type="button"
              className="instruction-btn"
              onClick={() => {
                if (
                  !inventoryOpen &&
                  !shopOpen &&
                  !skillsOpen &&
                  !fittingOpen &&
                  !craftingOpen
                ) {
                  if (inRift) {
                    setRiftNavigatorOpen(true);
                    enqueueToast(
                      "Use the Rift Navigator to view your location in rifts",
                      "warning",
                    );
                  } else {
                    onOpenWorldMap(position);
                  }
                }
              }}
            >
              [M]ap
            </button>
            <span className="separator">|</span>
            <button
              type="button"
              className="instruction-btn"
              onClick={() => {
                if (
                  !inventoryOpen &&
                  !shopOpen &&
                  !skillsOpen &&
                  !fittingOpen &&
                  !craftingOpen &&
                  !expressionPanelOpen &&
                  !fleetPanelOpen
                ) {
                  setExpressionPanelOpen(true);
                }
              }}
            >
              [P]layer
            </button>
            <span className="separator">|</span>
            <button
              type="button"
              className="instruction-btn"
              onClick={() => {
                if (
                  !inventoryOpen &&
                  !shopOpen &&
                  !skillsOpen &&
                  !fittingOpen &&
                  !craftingOpen &&
                  !expressionPanelOpen &&
                  !fleetPanelOpen
                ) {
                  setQuestLogOpen(true);
                }
              }}
            >
              [Q]uests
            </button>
            <span className="separator">|</span>
            <button
              type="button"
              className="instruction-btn"
              onClick={() => {
                if (
                  !inventoryOpen &&
                  !shopOpen &&
                  !skillsOpen &&
                  !fittingOpen &&
                  !craftingOpen &&
                  !expressionPanelOpen &&
                  !fleetPanelOpen
                ) {
                  setChatOpen(true);
                }
              }}
            >
              [ENTER] Chat
            </button>
            <span className="separator">|</span>
            <button
              type="button"
              className="instruction-btn"
              onClick={() => {
                setInboxOpen(true);
              }}
            >
              Inbox
              {unreadMailCount > 0 && (
                <span
                  style={{
                    display: "inline-block",
                    width: "8px",
                    height: "8px",
                    backgroundColor: "#22c55e",
                    marginLeft: "6px",
                    borderRadius: "1px",
                  }}
                />
              )}
            </button>
            <span className="separator">|</span>
          </footer>
        </main>

        {/* Dock proximity prompt (hide only when core overlays are open) */}
        {dockPrompt &&
          !inventoryOpen &&
          !shopOpen &&
          !skillsOpen &&
          !fittingOpen &&
          !craftingOpen && (
            <div
              style={{
                position: "fixed",
                right: "16px",
                bottom: "120px",
                background: "#0d1117",
                border: "1px solid #58a6ff",
                padding: "10px 12px",
                color: "#e6edf3",
                fontFamily: "var(--font-mono), monospace",
                minWidth: "220px",
                boxShadow: "0 0 12px rgba(88,166,255,0.35)",
                zIndex: 1200,
              }}
            >
              <div style={{ fontSize: "12px", marginBottom: "6px" }}>
                Dock at{" "}
                <span style={{ color: "#58a6ff", fontWeight: 700 }}>
                  {dockPrompt.islandName}
                </span>
                ?
              </div>
              <button
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #238636",
                  background: "#238636",
                  color: "#fff",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontWeight: 700,
                }}
                onClick={() => {
                  const scene = gameRef.current?.scene.getScene(
                    "OceanScene",
                  ) as any;
                  scene?.dockAtIsland?.(dockPrompt.islandId);
                }}
              >
                DOCK
              </button>
            </div>
          )}

        {/* Inventory Panel */}
        <InventoryPanel
          socket={socketRef.current}
          isOpen={inventoryOpen}
          onClose={() => setInventoryOpen(false)}
          pieces={pieces}
          onInventoryUpdate={setInventoryItems}
          onOpenWildcardPowers={() => setSignaturePanelOpen(true)}
          hasEssence={hasEssenceEquipped}
        />

        <LootContainerPanel
          socket={socketRef.current}
          isOpen={lootOpen}
          containerId={lootContainerId}
          onClose={() => {
            setLootOpen(false);
            setLootContainerId(null);
          }}
        />

        {/* Marketplace Panel - Only opens at Trading Posts */}
        <MarketplaceOverlay
          isOpen={shopOpen}
          onClose={() => setShopOpen(false)}
        >
          <MarketplacePanel
            socket={socketRef.current}
            isOpen={shopOpen}
            onClose={() => setShopOpen(false)}
            pieces={pieces}
            onPiecesChange={setPieces}
            tradingPostName={currentTradingPost}
            onSetDestination={(x: number, y: number, name: string) => {
              socketRef.current?.emit("shipMoveTo", { x, y });
              console.log(`Setting destination to ${name} at (${x}, ${y})`);
            }}
            locationId={dockedIslandId || ""}
            locationType={
              dockedIslandIsHarborOffice ? "harbor_office" : "trading_post"
            }
            hasCourier={
              (dockedIslandId || "").includes("trading_post") ||
              (dockedIslandId || "").includes("harbor_office")
            }
          />
        </MarketplaceOverlay>

        {/* Shipyard Panel - Only opens at Shipyards */}
        <ShipyardPanel
          socket={socketRef.current}
          isOpen={shipyardOpen}
          onClose={() => setShipyardOpen(false)}
          pieces={pieces}
          onPiecesChange={setPieces}
          shipyardName={currentShipyard}
        />

        {/* Dev Admin Panel - fixed to browser bottom-right */}
        {/* Show in dev mode OR for admin users in production */}
        {(process.env.NODE_ENV !== "production" || isAdmin) && apiBaseUrl && (
          <AdminPanel
            key={devAdminPanelInstanceKeyRef.current}
            apiBaseUrl={apiBaseUrl}
            socket={socketRef.current}
            playerId={playerId || socketRef.current?.id}
            currentTile={tilePos}
            onBerriesChange={setPieces}
            setLoadingOverlayVisible={setLoadingOverlayVisible}
            setLoadingOverlayMessage={setLoadingOverlayMessage}
            setIsInstanceEntry={setIsInstanceEntry}
            onToggleCollisionDebug={(enabled: boolean) => {
              const scene = gameRef.current?.scene.getScene(
                "OceanScene",
              ) as any;
              if (
                scene &&
                typeof (scene as any).setCollisionDebugEnabled === "function"
              ) {
                (scene as any).setCollisionDebugEnabled(enabled);
              }
            }}
            onToggleHitboxDebug={(enabled: boolean) => {
              const scene = gameRef.current?.scene.getScene(
                "OceanScene",
              ) as any;
              if (
                scene &&
                typeof (scene as any).setHitboxDebugEnabled === "function"
              ) {
                (scene as any).setHitboxDebugEnabled(enabled);
              }
            }}
          />
        )}

        {isModerator && (
          <ModeratorPanel
            socket={socketRef.current}
            isOpen={moderatorPanelOpen}
            onClose={() => setModeratorPanelOpen(false)}
          />
        )}

        {/* Debug Panel - Press F3 to toggle */}
        <DebugPanel
          playerId={playerId || socketRef.current?.id || null}
          apiBaseUrl={apiBaseUrl}
          isVisible={debugPanelOpen}
          onClose={() => setDebugPanelOpen(false)}
          socket={socketRef.current}
        />

        {/* Island Research Panel - Opens on right-click island */}
        <IslandResearchPanel
          isOpen={researchPanelOpen}
          onClose={() => {
            setResearchPanelOpen(false);
            setResearchIslandId(null);
            setResearchIslandPosition(null);
          }}
          islandId={researchIslandId}
          islandPosition={researchIslandPosition}
          playerPosition={position}
          refreshNonce={researchRefreshNonce}
          onClaimTerritory={(islandData) => {
            setClaimIslandData({
              id: islandData.id,
              name: islandData.customName || islandData.name,
              type: islandData.type as
                | "tiny"
                | "small"
                | "medium"
                | "large"
                | "massive",
              biome: islandData.biome,
              isPermanent: islandData.isPermanent,
              isClaimed: islandData.isClaimed,
              ownerName: islandData.ownerName,
              ownerType: islandData.ownerType,
              customName: islandData.customName,
            });
            setResearchPanelOpen(false);
            setTerritoryClaimOpen(true);
          }}
          onStartSiege={(islandId, islandName) => {
            setSiegeIslandId(islandId);
            setSiegeIslandName(islandName);
            setResearchPanelOpen(false);
            setSiegePanelOpen(true);
          }}
          onManageTerritory={(islandId, islandName, isOwner) => {
            setManagementIslandId(islandId);
            setManagementIslandName(islandName);
            setManagementIsOwner(isOwner);
            setResearchPanelOpen(false);
            setTerritoryManagementOpen(true);
          }}
          onViewStatus={(islandId, islandName) => {
            setStatusIslandId(islandId);
            setStatusIslandName(islandName);
            setResearchPanelOpen(false);
            setTerritoryStatusOpen(true);
          }}
          playerId={playerInfo.characterId || playerId}
          canDock={researchCanDock}
          isDocked={researchIsDocked}
          isDockedHere={researchIsDockedHere}
          onDock={(islandId) => {
            const scene = gameRef.current?.scene.getScene("OceanScene") as
              | OceanScene
              | undefined;
            scene?.dockAtIsland(islandId);
            setResearchPanelOpen(false);
          }}
          onUndock={() => {
            const scene = gameRef.current?.scene.getScene("OceanScene") as
              | OceanScene
              | undefined;
            scene?.undockFromIsland();
            setResearchPanelOpen(false);
          }}
        />

        {/* Territory Claim Panel - Opens from Island Research */}
        <TerritoryClaimPanel
          isOpen={territoryClaimOpen}
          onClose={() => {
            setTerritoryClaimOpen(false);
            setClaimIslandData(null);
          }}
          socket={socketRef.current}
          island={claimIslandData}
          playerBerries={pieces}
          playerName={playerInfo.name}
        />

        {/* Siege Panel - Territory contestation minigame */}
        <SiegePanel
          isOpen={siegePanelOpen}
          onClose={() => {
            setSiegePanelOpen(false);
            setSiegeIslandId("");
            setSiegeIslandName("");
          }}
          socket={socketRef.current}
          islandId={siegeIslandId}
          islandName={siegeIslandName}
          playerId={playerInfo.characterId || playerId || ""}
          playerName={playerInfo.name}
        />

        {/* Territory Management Panel - Build structures and harvest resources */}
        <TerritoryManagementPanel
          isOpen={territoryManagementOpen}
          onClose={() => {
            setTerritoryManagementOpen(false);
            setManagementIslandId("");
            setManagementIslandName("");
            setManagementIsOwner(false);
          }}
          socket={socketRef.current}
          islandId={managementIslandId}
          islandName={managementIslandName}
          isOwner={managementIsOwner}
          playerPieces={pieces}
        />

        {/* Territory Status Panel - Comprehensive overview with garrison and permissions */}
        <TerritoryStatusPanel
          isOpen={territoryStatusOpen}
          onClose={() => {
            setTerritoryStatusOpen(false);
            setStatusIslandId("");
            setStatusIslandName("");
          }}
          socket={socketRef.current}
          islandId={statusIslandId}
          islandName={statusIslandName}
          playerPieces={pieces}
        />

        {/* Territory Overview Panel - Standalone overlay showing all territories */}
        <TerritoryOverviewPanel />

        {/* Fishing Spot Research Panel - Opens on right-click fishing spot */}
        <FishingSpotResearchPanel
          isOpen={fishingResearchOpen}
          onClose={() => {
            setFishingResearchOpen(false);
            setFishingSpotId(null);
            setFishingSpotType(null);
            setFishingSpotPosition(null);
          }}
          spotId={fishingSpotId}
          spotType={fishingSpotType}
          spotPosition={fishingSpotPosition}
          playerPosition={position}
        />

        {/* Resource Node Research Panel - Opens on right-click resource node -> Research */}
        <ResourceNodeResearchPanel
          isOpen={resourceResearchOpen}
          onClose={() => {
            setResourceResearchOpen(false);
            setResourceNodeId(null);
            setResourceNodeType(null);
            setResourceNodePosition(null);
          }}
          nodeId={resourceNodeId}
          nodeType={resourceNodeType}
          nodePosition={resourceNodePosition}
          playerPosition={position}
        />

        {/* Whirlpool Research Panel - Opens on right-click whirlpool -> Scan */}
        <WhirlpoolResearchPanel
          isOpen={whirlpoolResearchOpen}
          onClose={() => {
            setWhirlpoolResearchOpen(false);
            setWhirlpoolId(null);
            setWhirlpoolPosition(null);
          }}
          whirlpoolId={whirlpoolId}
          whirlpoolPosition={whirlpoolPosition}
          playerPosition={position}
          onEnterWhirlpool={(entryId: string) => {
            socketRef.current?.emit("whirlpool:enter", { entryId });
          }}
        />

        {/* Rift Scar Research Panel - Opens on right-click rift scar -> Research */}
        {riftScarData && (
          <RiftScarResearchPanel
            scarData={riftScarData}
            onClose={() => {
              setRiftScarData(null);
            }}
            apiBaseUrl={apiBaseUrl}
          />
        )}

        {/* Whirlpool HUD - Shows when inside a whirlpool instance */}
        {inWhirlpool && whirlpoolInstance && (
          <WhirlpoolHUD
            instance={whirlpoolInstance}
            playerPosition={position}
            expiresAt={whirlpoolExpiresAt}
            onExit={() => {
              socketRef.current?.emit("whirlpool:exit");
            }}
          />
        )}

        {/* Rift Storm HUD - Shows button when inside a rift storm */}
        {currentRiftStormId && (
          <div
            style={{
              position: "fixed",
              top: 12,
              right: 12,
              background: "#0d1117",
              border: "1px solid #30363d",
              padding: "10px 12px",
              zIndex: 3200,
              boxShadow: "0 0 10px rgba(0,0,0,0.45)",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              fontFamily: "var(--font-mono)",
            }}
          >
            <div style={{ color: "#ffd700", fontSize: 12, fontWeight: 700 }}>
              RIFT STORM {currentRiftStormTier.toUpperCase()}
            </div>
            <div style={{ fontSize: 11, color: "#a0aec0", marginBottom: 4 }}>
              Loot: +{Math.round((currentRiftStormLootMultiplier - 1) * 100)}% |
              XP: +{Math.round((currentRiftStormXpMultiplier - 1) * 100)}%
            </div>
            <button
              type="button"
              style={{
                background: "#3182ce",
                color: "#ffffff",
                border: "1px solid #265bbd",
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
              }}
              onClick={() => setRiftRelicPanelOpen(true)}
            >
              Crack Relics
            </button>
          </div>
        )}

        {/* Docked Panel - Shows when docked at an island */}
        <DockedPanel
          isOpen={isDocked && !!dockedIslandId}
          islandName={dockedIslandName}
          islandId={dockedIslandId || ""}
          onClose={() => {
            const scene = gameRef.current?.scene.getScene("OceanScene") as
              | OceanScene
              | undefined;
            scene?.undockFromIsland();
          }}
          onUndock={() => {
            const scene = gameRef.current?.scene.getScene("OceanScene") as
              | OceanScene
              | undefined;
            scene?.undockFromIsland();
          }}
          onOpenShop={() => {
            setCurrentTradingPost(dockedIslandName);
            setShopOpen(true);
          }}
          onOpenShipyard={() => {
            setCurrentShipyard(dockedIslandName);
            setShipyardOpen(true);
          }}
          onOpenCourier={() => {
            setCourierPanelOpen(true);
          }}
          onOpenTavern={() => {
            setTavernOpen(true);
          }}
          onOpenBank={() => {
            setBankOpen(true);
          }}
          onUseDockServices={() => {
            setDockServicesPanelOpen(true);
          }}
          onTalkToNPC={(npcId, npcName) => {
            socketRef.current?.emit("quest:npc_interaction", {
              npcId,
              locationId: dockedIslandId,
            });
            setNpcDialogueOpen(true);
            setNpcDialogueData({
              npcId,
              npcName,
              npcTitle: "Merchant",
              npcPortrait: "/assets/portraits/merchants/merchant_1.png",
              dialogue: `Welcome, captain. I'm ${npcName}. What brings you to these waters?`,
            });
          }}
          onOpenResearch={() => {
            setResearchPanelOpen(true);
            setResearchIslandId(dockedIslandId);
            setResearchIslandPosition(null);
          }}
          onOpenTerritory={() => {
            setClaimIslandData({
              id: dockedIslandId || "",
              name: dockedIslandName,
              type: "small",
              biome: "temperate",
              isPermanent: false,
              isClaimed: false,
              ownerName: "",
              ownerType: "player",
              customName: dockedIslandName,
            });
            setTerritoryClaimOpen(true);
          }}
          onOpenFleet={
            dockedIslandIsHarborOffice
              ? () => {
                  setFleetPanelOpen(true);
                }
              : undefined
          }
          hasShop={dockedIslandHasShop}
          hasShipyard={dockedIslandHasShipyard}
          hasCourier={dockedIslandHasCourier}
          hasTavern={dockedIslandHasTavern}
          hasDockServices={dockedIslandHasDockServices}
          isHarborOffice={dockedIslandIsHarborOffice}
          canResearch={
            !dockedIslandId?.includes("trading_post") &&
            !dockedIslandId?.includes("shipyard")
          }
          canClaimTerritory={
            !dockedIslandId?.includes("trading_post") &&
            !dockedIslandId?.includes("shipyard") &&
            !dockedIslandId?.includes("tavern") &&
            !dockedIslandIsPermanent
          }
          npcs={dockedIslandNpcs}
        />

        {/* Dock Services Panel */}
        <DockServicesPanel
          isOpen={dockServicesPanelOpen}
          onClose={() => setDockServicesPanelOpen(false)}
          shipType={
            shipType as
              | "raft"
              | "sloop"
              | "caravel"
              | "brigantine"
              | "galleon"
              | "man_o_war"
          }
          currentHeat={currentShipHeat}
          maxHeat={maxShipHeat}
          currentHullHp={playerHp.hull}
          maxHullHp={playerHp.maxHull}
          currentSailHp={playerHp.sail}
          maxSailHp={playerHp.maxSail}
          berries={pieces}
          onReduceHeat={() => {
            socketRef.current?.emit("ship:dockServices");
          }}
          onReplenishHealth={() => {
            socketRef.current?.emit("ship:replenishHealth");
          }}
          onUpgradeShip={() => {
            console.log("🚀 Emitting ship:upgrade event");
            setIsUpgradingShip(true);
            socketRef.current?.emit("ship:upgrade");
          }}
          isUpgrading={isUpgradingShip}
        />

         {hasEssenceEquipped && (
           <>
             <SignaturePowerBar socket={socketRef.current} />
             <WildcardPowerPanel
               isOpen={signaturePanelOpen}
               onClose={() => setSignaturePanelOpen(false)}
               socket={socketRef.current}
             />
           </>
         )}

        {currentRiftStormId && (
          <RiftRelicPanel
            socket={socketRef.current}
            isOpen={riftRelicPanelOpen}
            onClose={() => setRiftRelicPanelOpen(false)}
            riftStormId={currentRiftStormId}
            riftStormTier={currentRiftStormTier}
            lootMultiplier={currentRiftStormLootMultiplier}
            xpMultiplier={currentRiftStormXpMultiplier}
          />
        )}

        {/* Player Expression Panel - Titles, flags, bio (Press P) */}
        {showPlayerIdentityUI && (
          <PlayerExpressionPanel
            isOpen={expressionPanelOpen}
            onClose={() => setExpressionPanelOpen(false)}
            socket={socketRef.current}
            playerLevel={level}
            playerInfamy={infamy}
          />
        )}

        {/* Referral Panel Modal */}
        {referralPanelOpen && playerInfo.characterId && (
          <ReferralPanel
            onClose={() => setReferralPanelOpen(false)}
            currentCharacterId={playerInfo.characterId}
            characters={auth.characters}
          />
        )}

        {/* Daily Rewards Panel Modal */}
        {dailyRewardsPanelOpen && playerInfo.characterId && (
          <GameDailyRewardsPanel
            onClose={() => setDailyRewardsPanelOpen(false)}
            currentCharacterId={playerInfo.characterId}
            characters={auth.characters}
          />
        )}

        {/* Guest Account On-Ramp Panel */}
        {!playerInfo.characterId && !guestOnrampDismissed && (
          <div
            className="font-mono"
            style={{
              position: "fixed",
              bottom: 16,
              right: 16,
              width: 280,
              backgroundColor: "rgba(13, 17, 23, 0.98)",
              border: "2px solid #1f2937",
              padding: 16,
              zIndex: 5000,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  color: "#fbbf24",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 1,
                }}
              >
                PLAYING AS GUEST
              </div>
              <button
                type="button"
                onClick={() => {
                  setGuestOnrampDismissed(true);
                  try {
                    localStorage.setItem("guestOnrampDismissed", "true");
                  } catch {
                    // ignore
                  }
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#6b7280",
                  cursor: "pointer",
                  fontSize: 14,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                x
              </button>
            </div>
            <div
              style={{
                color: "#9ca3af",
                fontSize: 11,
                marginBottom: 12,
                lineHeight: 1.4,
              }}
            >
              Your progress is saved locally. Create an account to save your
              character permanently and unlock referral rewards!
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                type="button"
                className="context-menu-item"
                style={{
                  width: "100%",
                  textAlign: "center",
                  backgroundColor: "#1d4ed8",
                  border: "1px solid #2563eb",
                }}
                onClick={() => {
                  window.location.href = "/login?migrate=true";
                }}
              >
                Create Account
              </button>
              <button
                type="button"
                className="context-menu-item"
                style={{ width: "100%", textAlign: "center", color: "#6b7280" }}
                onClick={() => {
                  setGuestOnrampDismissed(true);
                  try {
                    localStorage.setItem("guestOnrampDismissed", "true");
                  } catch {
                    // ignore
                  }
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        )}

        {/* Fleet Panel - Guild/Corporation management (Press G) */}
        <FleetPanel
          isOpen={fleetPanelOpen}
          onClose={() => setFleetPanelOpen(false)}
          socket={socketRef.current}
          playerBerries={pieces}
          playerLevel={level}
          playerName={playerInfo.name}
          characterId={playerInfo.characterId || playerId || ""}
          isDockedAtHarborOffice={dockedIslandIsHarborOffice}
          harborOfficeName={
            dockedIslandIsHarborOffice ? dockedIslandName : undefined
          }
        />

        {/* World History Panel - Shows legendary pirates */}
        <WorldHistoryPanel
          socket={socketRef.current}
          isOpen={worldHistoryOpen}
          onClose={() => setWorldHistoryOpen(false)}
        />

        {/* Skills Panel - Runescape-style skill levels */}
        <SkillsPanel
          socket={socketRef.current}
          isOpen={skillsOpen}
          onClose={() => setSkillsOpen(false)}
        />

        {/* Ship Fitting Panel - EVE-style module fitting */}
        <ShipFittingPanel
          socket={socketRef.current}
          isOpen={fittingOpen}
          onClose={() => setFittingOpen(false)}
          shipId={shipId}
          shipType={shipType}
          pieces={pieces}
          isDockedAtShipyard={shipyardOpen}
        />

        {/* Crafting Panel - Module and item crafting */}
        <CraftingPanel
          socket={socketRef.current}
          isOpen={craftingOpen}
          onClose={() => setCraftingOpen(false)}
          playerLevel={level}
          pieces={pieces}
          unlockedBlueprints={unlockedBlueprints}
        />

        {/* Courier Panel - Courier contracts at trading posts and harbor offices */}
        <CourierPanel
          socket={socketRef.current}
          isOpen={courierPanelOpen}
          onClose={() => setCourierPanelOpen(false)}
          locationId={dockedIslandId || ""}
          locationName={dockedIslandName}
          locationType={
            dockedIslandIsHarborOffice ? "harbor_office" : "trading_post"
          }
          onSetDestination={(x, y, name) => {
            if (onSetDestination) {
              onSetDestination({ x, y, name });
            }
          }}
        />

        {/* Tavern Panel - Crew recruitment at taverns */}
        <TavernPanel
          socket={socketRef.current}
          isOpen={tavernOpen}
          onClose={() => setTavernOpen(false)}
          pieces={pieces}
          tavernId={dockedIslandId || ""}
          tavernName={dockedIslandName}
        />

        {/* Cooking Panel - Skill training at taverns */}
        <CookingPanel
          socketRef={socketRef}
          isOpen={cookingOpen}
          onClose={() => setCookingOpen(false)}
        />

        {/* Smithing Panel - Skill training at forges */}
        <SmithingPanel
          socketRef={socketRef}
          isOpen={smithingOpen}
          onClose={() => setSmithingOpen(false)}
        />

        {/* Alchemy Panel - Skill training at alchemy labs */}
        <AlchemyPanel
          socketRef={socketRef}
          isOpen={alchemyOpen}
          onClose={() => setAlchemyOpen(false)}
        />

        {/* Construction Hotbar - Quick-equip hotbar (1-9 keys) */}
        <ConstructionHotbar
          socket={socketRef.current}
          sceneRef={{
            current: gameRef.current?.scene.getScene("OceanScene"),
          }}
          isConstructionActive={constructionHotbarActive}
          currentKitId={currentConstructionKitId}
        />

        {/* Bank Panel - Merchant's Vault at all permanent locations */}
        <BankPanel
          socket={socketRef.current}
          isOpen={bankOpen}
          onClose={() => setBankOpen(false)}
          itemDefinitions={
            Object.fromEntries(
              inventoryItems.map((item: any) => {
                const def = item.definition || {
                  id: item.itemId,
                  name: "Unknown Item",
                  description: "",
                  type: "material",
                  rarity: "common",
                  icon: "/assets/unknown.png",
                  stackable: true,
                  maxStack: 99,
                };
                return [item.itemId, def as any];
              }),
            ) as Record<string, any>
          }
        />

        {/* Ambient Trade Panel - Sailor/Fisherman (Trading Post-style UI) */}
        {ambientTradeData && (
          <AmbientTradePanel
            socket={socketRef.current}
            isOpen={ambientTradeOpen}
            onClose={() => setAmbientTradeOpen(false)}
            pieces={pieces}
            ambientId={ambientTradeData.ambientId}
            traderName={ambientTradeData.traderName}
            items={ambientTradeData.items}
          />
        )}

        {/* Feedback Modal */}
        <FeedbackModal
          socket={socketRef.current}
          isOpen={feedbackModalOpen}
          onClose={() => setFeedbackModalOpen(false)}
          playerName={playerInfo.name}
          playerLevel={level}
        />

        {/* Options Menu */}
        <OptionsMenu
          isOpen={optionsOpen}
          onClose={() => setOptionsOpen(false)}
          volume={volume}
          sfxVolume={sfxVolume}
          musicVolume={musicVolume}
          cameraZoom={cameraZoom}
          showPlayerIdentityUI={showPlayerIdentityUI}
          showMyNameLabel={showMyNameLabel}
          showMyFlag={showMyFlag}
          showOtherPlayerNames={showOtherPlayerNames}
          showOtherPlayerFlags={showOtherPlayerFlags}
          backgroundMusicEnabled={backgroundMusicEnabled}
          worldEventNotificationsEnabled={worldEventNotificationsEnabled}
          serverFeedbackEnabled={serverFeedbackEnabled}
          onVolumeChange={setVolume}
          onSfxVolumeChange={setSfxVolume}
          onMusicVolumeChange={setMusicVolume}
          onZoomChange={setCameraZoom}
          onShowPlayerIdentityUIChange={setShowPlayerIdentityUI}
          onShowMyNameLabelChange={setShowMyNameLabel}
          onShowMyFlagChange={setShowMyFlag}
          onShowOtherPlayerNamesChange={setShowOtherPlayerNames}
          onShowOtherPlayerFlagsChange={setShowOtherPlayerFlags}
          onBackgroundMusicEnabledChange={setBackgroundMusicEnabled}
          onWorldEventNotificationsEnabledChange={
            setWorldEventNotificationsEnabled
          }
          onServerFeedbackEnabledChange={setServerFeedbackEnabled}
          onShowDailyRewards={() => setDailyRewardsPanelOpen(true)}
          onShowReferral={() => setReferralPanelOpen(true)}
        />

        {/* Quest Tracker Panel - Shows current active quest */}
        {!starterVoyageHidden && (
          <QuestTrackerPanel
            socket={socketRef.current}
            onClose={() => setStarterVoyageHidden(true)}
            minimized={starterVoyageMinimized}
            onToggleMinimize={() =>
              setStarterVoyageMinimized(!starterVoyageMinimized)
            }
          />
        )}

        {/* Infamy Info Modal */}
        {infamyInfoOpen && (
          <div
            className="inventory-overlay"
            onClick={() => setInfamyInfoOpen(false)}
          >
            <div
              className="infamy-info-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="inventory-header">
                <h2>INFAMY SYSTEM</h2>
                <button
                  className="close-btn"
                  onClick={() => setInfamyInfoOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="infamy-content">
                <section className="info-section">
                  <h3>What is Infamy?</h3>
                  <p>
                    Infamy represents your notoriety across the seas. As you
                    defeat enemies and cause chaos, your infamy grows,
                    attracting both fame and danger.
                  </p>
                </section>

                <section className="info-section">
                  <h3>Infamy Tiers</h3>
                  <div className="tier-list">
                    <div className="tier-item">
                      <span className="tier-name" style={{ color: "#10b981" }}>
                        Deckhand
                      </span>
                      <span className="tier-range">0 - 999</span>
                    </div>
                    <div className="tier-item">
                      <span className="tier-name" style={{ color: "#22c55e" }}>
                        Raider
                      </span>
                      <span className="tier-range">1,000 - 9,999</span>
                    </div>
                    <div className="tier-item">
                      <span className="tier-name" style={{ color: "#a3e635" }}>
                        Marauder
                      </span>
                      <span className="tier-range">10,000 - 49,999</span>
                    </div>
                    <div className="tier-item">
                      <span className="tier-name" style={{ color: "#facc15" }}>
                        Scourge
                      </span>
                      <span className="tier-range">50,000 - 99,999</span>
                    </div>
                    <div className="tier-item">
                      <span className="tier-name" style={{ color: "#f97316" }}>
                        Dread Captain
                      </span>
                      <span className="tier-range">100,000 - 499,999</span>
                    </div>
                    <div className="tier-item">
                      <span className="tier-name" style={{ color: "#ea580c" }}>
                        Warlord
                      </span>
                      <span className="tier-range">500,000 - 999,999</span>
                    </div>
                    <div className="tier-item">
                      <span className="tier-name" style={{ color: "#dc2626" }}>
                        Emperor of the Sea
                      </span>
                      <span className="tier-range">1,000,000+</span>
                    </div>
                  </div>
                </section>

                <section className="info-section">
                  <h3>[HEAT] Heat Level</h3>
                  <p>Heat indicates how aggressively NPCs will pursue you:</p>
                  <ul className="heat-effects">
                    <li>
                      <span style={{ color: "#10b981" }}>MINIMAL</span> - NPCs
                      ignore you unless provoked
                    </li>
                    <li>
                      <span style={{ color: "#22c55e" }}>LOW</span> - Occasional
                      Navy patrols may investigate
                    </li>
                    <li>
                      <span style={{ color: "#a3e635" }}>MODERATE</span> - Navy
                      ships actively patrol your area
                    </li>
                    <li>
                      <span style={{ color: "#facc15" }}>ELEVATED</span> -
                      Bounty hunters start appearing
                    </li>
                    <li>
                      <span style={{ color: "#f97316" }}>HIGH</span> - Navy
                      fleets dispatched to hunt you
                    </li>
                    <li>
                      <span style={{ color: "#ea580c" }}>EXTREME</span> -
                      Admirals may personally intervene
                    </li>
                    <li>
                      <span style={{ color: "#dc2626" }}>MAXIMUM</span> - The
                      entire Navy is after you!
                    </li>
                  </ul>
                </section>

                <section className="info-section">
                  <h3>Gaining Infamy</h3>
                  <ul>
                    <li>
                      Defeating enemy ships (+infamy based on enemy level)
                    </li>
                    <li>Sinking Navy vessels (+bonus infamy)</li>
                    <li>Completing bounty hunts</li>
                    <li>PvP victories against other pirates</li>
                  </ul>
                </section>

                <section className="info-section">
                  <h3>Benefits of High Infamy</h3>
                  <ul>
                    <li>Access to exclusive high-tier content</li>
                    <li>Recognition on the World History leaderboard</li>
                    <li>Stronger NPC crews may join you</li>
                    <li>Better loot drops from defeated enemies</li>
                  </ul>
                </section>
              </div>
            </div>
          </div>
        )}

        {/* Cannon Focus Minigame - React-based overlay */}
        <CannonFocusOverlay
          isActive={cannonFocusActive}
          targetName={cannonFocusTarget.name}
          targetHp={cannonFocusTarget.hp}
          targetMaxHp={cannonFocusTarget.maxHp}
          targetFaction={cannonFocusTarget.faction}
          targetLevel={cannonFocusTarget.level}
          playerHp={playerHp.hull}
          playerMaxHp={playerHp.maxHull}
          playerSP={playerSP.current}
          playerMaxSP={playerSP.max}
          playerLevel={level}
          onBonusUpdate={(multiplier: number, defenseModifier: number) => {
            // Send multiplier and defense modifier to server for combat calculation
            socketRef.current?.emit("cannonFocusUpdate", {
              multiplier,
              defenseModifier,
            });
          }}
          onDeactivate={() => {
            setCannonFocusActive(false);
            socketRef.current?.emit("cannonFocusUpdate", {
              multiplier: 1.0,
              defenseModifier: 1.0,
            });
          }}
        />

        {/* Skill Level Up Notifications */}
        <SkillLevelUpNotification />

        {/* Fishing Minigame - React-based overlay */}
        <FishingMinigame
          isActive={fishingMinigameActive}
          spotType={fishingSpotTypeForMinigame}
          spotId={fishingSpotIdForMinigame}
          onCatchComplete={(quality: number) => {
            console.log(
              `🎣 GameCanvas: Fishing minigame complete with quality ${quality}%, emitting to server`,
            );
            socketRef.current?.emit("fishing:minigameComplete", {
              spotId: fishingSpotIdForMinigame,
              quality,
            });
          }}
          onDeactivate={() => {
            console.log("🎣 GameCanvas: Fishing minigame deactivated");
            setFishingMinigameActive(false);
          }}
        />

        {/* Mining Minigame - React-based overlay */}
        <MiningMinigame
          isActive={miningMinigameActive}
          nodeName={miningNodeName}
          durationSeconds={miningDurationSeconds}
          difficultyLevel={miningNodeLevel}
          onSuccess={completeMiningHarvest}
          onFailure={failMiningHarvest}
          onCancel={cancelMiningHarvest}
        />

        {/* Bottle Content Modal - Message in a Bottle UI */}
        {bottleContent && (
          <BottleContentModal
            isOpen={bottleModalOpen}
            type={bottleContent.type}
            content={bottleContent.content}
            reward={bottleContent.reward}
            onDismiss={() => {
              setBottleModalOpen(false);
              setBottleContent(null);
            }}
          />
        )}

        {/* Ship Investigation Panel */}
        <ShipInvestigationPanel
          isOpen={shipInvestigationOpen}
          shipData={investigatedShip}
          onClose={() => {
            setShipInvestigationOpen(false);
            setInvestigatedShip(null);
          }}
        />

        {/* Quest Log Panel */}
        {questLogOpen && (
          <QuestLogPanel
            socket={socketRef.current}
            onClose={() => setQuestLogOpen(false)}
            quests={STARTER_VOYAGE_QUESTS}
            onSetDestination={(x, y, name) => {
              if (onSetDestination) {
                onSetDestination({ x, y, name });
              }
            }}
          />
        )}

        {/* Quests Panel */}
        <QuestsPanel
          isOpen={questsPanelOpen}
          onClose={() => setQuestsPanelOpen(false)}
          quests={{}}
          questDefinitions={STARTER_VOYAGE_QUESTS}
          starterVoyageComplete={false}
          onSetDestination={(x, y, name) => {
            if (onSetDestination) {
              onSetDestination({ x, y, name });
            }
          }}
          onOpenQuestJournal={(questId) => {
            setSelectedQuestForJournal(questId);
            setQuestJournalOpen(true);
          }}
          trackedQuestId={trackedQuestId}
          onTrackQuest={(questId) => {
            setTrackedQuestId(trackedQuestId === questId ? null : questId);
          }}
        />

        {/* Quest Journal */}
        {questJournalOpen && selectedQuestForJournal && (
          <QuestJournal
            isOpen={questJournalOpen}
            onClose={() => {
              setQuestJournalOpen(false);
              setSelectedQuestForJournal(null);
            }}
            quest={
              STARTER_VOYAGE_QUESTS.find(
                (q) => q.id === selectedQuestForJournal,
              ) || null
            }
          />
        )}

        {/* NPC Dialogue Modal */}
        {npcDialogueData && (
          <NPCDialogueModal
            isOpen={npcDialogueOpen}
            npcId={npcDialogueData.npcId}
            npcName={npcDialogueData.npcName}
            npcTitle={npcDialogueData.npcTitle}
            npcPortrait={npcDialogueData.npcPortrait}
            dialogue={npcDialogueData.dialogue}
            questDialogue={npcDialogueData.questDialogue}
            questProgress={npcDialogueData.questProgress}
            onClose={() => {
              setNpcDialogueOpen(false);
              setNpcDialogueData(null);
            }}
          />
        )}

        {/* Player Inbox */}
        {inboxOpen && (
          <PlayerInbox
            socket={socketRef.current}
            onClose={() => setInboxOpen(false)}
          />
        )}

        {/* Sanctum overlay removed - exits are now in-world interactables */}

        {/* Memory Lattice - modal triggered by clicking terminal object in Sanctum */}
        <ArchitectConduit
          isOpen={memoryLatticeOpen}
          onClose={() => setMemoryLatticeOpen(false)}
          terminalData={sanctumState.terminalData}
        />

        {/* Global Context Menu Renderer */}
        <ContextMenuRenderer gameRef={gameRef} />

        {/* Ship Tooltip - React-based hover tooltip for ships */}
        {shipTooltip && (
          <ShipTooltip
            visible={shipTooltip.visible}
            x={shipTooltip.x}
            y={shipTooltip.y}
            shipData={shipTooltip.shipData}
            localPlayerId={playerId || ""}
            localShipPosition={position}
            shipPosition={shipTooltip.shipPosition}
            portraitIndex={shipTooltip.portraitIndex}
          />
        )}

        {/* Whirlpool Tooltip - React-based hover tooltip for whirlpools */}
        <WhirlpoolTooltip
          entry={whirlpoolTooltip}
          isVisible={whirlpoolTooltipVisible}
        />

        {/* Resource Node Tooltip - React-based hover tooltip for resource nodes */}
        {resourceNodeTooltip && (
          <ResourceNodeTooltip
            name={resourceNodeTooltip.name}
            requirement={resourceNodeTooltip.requirement}
            x={resourceNodeTooltip.x}
            y={resourceNodeTooltip.y}
            visible={resourceNodeTooltipVisible}
          />
        )}

        {/* Disconnection Overlay - blocks input when disconnected */}
        <DisconnectionOverlay
          isDisconnected={isDisconnected}
          reconnectAttempt={reconnectAttempt}
        />

        {/* Duplicate Connection Overlay - shows when logged in from another location */}
        <DuplicateConnectionOverlay show={duplicateConnectionError} />

        {/* Rift Navigator - Phaser-based like WorldMap and FactionMap */}
        {riftNavigatorOpen && (
          <RiftNavigatorWrapper
            playerId={playerId || "unknown"}
            onClose={() => setRiftNavigatorOpen(false)}
            playerPosition={position}
          />
        )}

        {/* Loading Overlay - shows during instance entries */}
        <LoadingOverlay
          isVisible={loadingOverlayVisible}
          isInstanceEntry={isInstanceEntry}
          message={loadingOverlayMessage}
        />
      </div>
    </GlobalTooltipProvider>
  );
};
